import { Navbar } from '@/components/Navbar'
import { AlertBanner } from '@/components/common/AlertBanner'
import { AuthorizingClinicianCard } from '@/components/order/AuthorizingClinicianCard'
import { OrderHeader } from '@/components/order/OrderHeader'
import { TermsDisclosureCard } from '@/components/order/TermsDisclosureCard'
import { FieldMarker } from '@/components/common/FieldMarkers'
import { resolveFieldMarkerState } from '@/components/common/fieldMarkerState'
import RequisitionFormView from '@/components/inProgressOrder/RequisitionFormView'
import { OrderFormViewModal } from '@/components/modals/OrderFormViewModal'
import { OrderSuccessModal } from '@/components/modals/OrderSuccessModal'
import { ShippingLabelModal, type ShippingLabelModalState } from '@/components/modals/ShippingLabelModal'
import {
  getClientName,
  getFulfillmentStrategy,
  getTrfOrgAddress,
  isTrfSignatureDocusignEnabled,
} from '@/config/uiConfig'
import { useUiConfig } from '@/config/uiConfigHooks'
import ProviderAPIClient, {
  OrderingSessionGoneError,
  type CoverageCheckRequest,
  type CoverageCheckResponse,
} from '@/features/auth/ProviderAPIClient'
import EpicOrderDiscarded from '@/features/auth/components/EpicOrderDiscarded'
import { DiscardOrderConfirmDialog } from '@/components/modals/DiscardOrderConfirmDialog'
import { useDiscardOrder } from '@/pages/order/hooks/useDiscardOrder'
import { isDiscardMarkerSet } from '@/features/ordering/discardMarker'
import {
  readEpicLaunchPatientId,
  readSessionPatientLookupId,
  resolvePatientIdForDraftLookup,
} from '@/utils/orderDraftPatientId'
import { useLaunchMode } from '@/features/auth/launchContext'
import { requestShippingLabel, resolveShippingAddresses } from '@/features/order/generateShippingLabel'
import { setEpicEhrEncounterForOrder } from '@/features/auth/epicRedoxContextStorage'
import { EpicAuthClient } from '@/features/auth/services/EpicAuthClient'
import { EpicSessionExpiryWarning } from '@/features/auth/components/EpicSessionExpiryWarning'
import { useAuthorization } from '@/features/authorization'
import {
  buildCanonicalPrimaryConsent,
  canonicalProvidersFromOrderFormInfo,
  guardianRelationshipToFamilyMember,
  legacyTrfPayloadToCanonical,
  mapCanonicalToOrderFormInfo,
  mapEpicContextToEhrContext,
  mergeCanonicalProviders,
  type CanonicalOrderPayload,
  type PrimaryConsentFamilyMember,
} from '@/ordering/canonicalOrder'
import { useAppDispatch, useAppSelector } from '@/store/hooks'
import { setCanonicalOrder } from '@/store/slices/canonicalOrderSlice'
import {
  AuthorizingProfessionalInfo,
  DEFAULT_CONSENT_FORM_DATA,
  OrderFormInfoData,
  PhysicianInfo,
  RequisitionFormData,
} from '@/types/RequisitionFormTypes'
import { applySelfProviderContact, type SelfProfile } from './selfProviderContact'
import { readyToSendSigningCopy } from './readyToSendSigningCopy'
import { orderSuccessSigningCopy, type SignatureRequestedCopy } from './orderSuccessSigningCopy'
import { cleanupPartialData } from '@/utils/DataCleanup'
import {
  createOrder,
  dedupeOrganizationPractitionersByPractitionerId,
  filterOrganizationPractitionersWithNpi,
  getOrganizationPractitioners,
  getOrderByPatient,
  type OrganizationPractitionerEntry,
  organizationPractitionerDisplayName,
  organizationPractitionerEntryNpi,
  organizationPractitionerRoleTitle,
  organizationPractitionerToAuthorizingSelection,
  OrderStatus,
  submitOrder,
} from '@/utils/DatabaseOperations'
import { extractFormOrderData } from '@/utils/FormDataExtraction'
import { getLocalCalendarDateString, getPatientInfoFromForm } from '@/utils/orderFormUtils'
import { resolveImdbOrgIdForOrderingApi } from '@/utils/orderingOrgId'
import { copyWithoutSigning } from '@/utils/copyWithoutSigning'
import { getCallerFromOrderData } from './orderCaller'
import { useAuth0 } from '@auth0/auth0-react'
import * as Sentry from '@sentry/react'
import { Check, InfoCircle, Search } from 'flowbite-react-icons/outline'
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom'
import {
  searchPatientEncounters,
  selectDefaultEncounterId,
  type PatientEncounterOption,
} from '@/features/auth/services/encounterSearchApi'
import { readPractitionerById, searchPractitioners } from '@/features/auth/services/practitionerSearchApi'
import {
  resolveEffectiveConsentFlowForSubmit,
  testLinesFromUiConfig,
  type DomainTestLine,
} from '@/pages/order/orderingSubmitValidation'
import {
  clinicalTextLimitError,
} from '@/utils/freeTextValidation'
import { markTrfSigningInProgress } from '@/pages/order/useTrfSigningInProgress'
import { trfSigningPath } from '@/features/docusign/signingPath'
import { useConfiguredOrderSteps } from '@/hooks/useConfiguredOrderSteps'




/** Figma shows the terms body without a "Summary:" label; strip it from config or fallbacks. */
function termsDisclosureSummary(configured: string | undefined, fallback: string): string {
  const raw = (configured?.trim() || fallback).trim()
  return raw.replace(/^Summary:\s*/i, '')
}

type ReadyToSendState =
  | {
      epicState?: string
      orderFormInfoData?: OrderFormInfoData | null
      formData?: RequisitionFormData | null
      orderId?: string | null
      /** Session ID from new ordering session flow (replaces legacy orderId for submit). */
      sessionId?: string | null
      authFlow?: string
      /** Ordering-domain configVersion — forwarded from OrderStart to avoid hardcoding. */
      configVersion?: string
      /** Parsed ordering-domain test lines (consentFlowId, primaryConsentKey per code). */
      orderingDomainTests?: DomainTestLine[]
    }
  | string
  | null

/**
 * Known US state name → USPS abbreviation map, plus DC and common territories, used to
 * normalize config/NPI state values for comparison regardless of whether they arrive as a
 * full name ("California") or an abbreviation ("CA").
 */
const US_STATE_NAME_TO_ABBR: Record<string, string> = {
  alabama: 'AL',
  alaska: 'AK',
  arizona: 'AZ',
  arkansas: 'AR',
  california: 'CA',
  colorado: 'CO',
  connecticut: 'CT',
  delaware: 'DE',
  'district of columbia': 'DC',
  florida: 'FL',
  georgia: 'GA',
  hawaii: 'HI',
  idaho: 'ID',
  illinois: 'IL',
  indiana: 'IN',
  iowa: 'IA',
  kansas: 'KS',
  kentucky: 'KY',
  louisiana: 'LA',
  maine: 'ME',
  maryland: 'MD',
  massachusetts: 'MA',
  michigan: 'MI',
  minnesota: 'MN',
  mississippi: 'MS',
  missouri: 'MO',
  montana: 'MT',
  nebraska: 'NE',
  nevada: 'NV',
  'new hampshire': 'NH',
  'new jersey': 'NJ',
  'new mexico': 'NM',
  'new york': 'NY',
  'north carolina': 'NC',
  'north dakota': 'ND',
  ohio: 'OH',
  oklahoma: 'OK',
  oregon: 'OR',
  pennsylvania: 'PA',
  'rhode island': 'RI',
  'south carolina': 'SC',
  'south dakota': 'SD',
  tennessee: 'TN',
  texas: 'TX',
  utah: 'UT',
  vermont: 'VT',
  virginia: 'VA',
  washington: 'WA',
  'west virginia': 'WV',
  wisconsin: 'WI',
  wyoming: 'WY',
  'puerto rico': 'PR',
}

/**
 * Normalize a US state string to its uppercase USPS abbreviation for equality comparison.
 * Accepts either a 2-letter code ("ca", "CA") or a full name ("California"), stripping periods
 * and surrounding whitespace. Returns `''` for empty/unknown input so callers can treat
 * missing and unmatched values uniformly (and never match an empty org state).
 */
function normalizeUsState(value?: string | null): string {
  const raw = value?.trim()
  if (!raw) return ''
  const cleaned = raw.replace(/\./g, '').trim()
  if (cleaned.length === 2) return cleaned.toUpperCase()
  return US_STATE_NAME_TO_ABBR[cleaned.toLowerCase()] ?? cleaned.toUpperCase()
}

/** NPI for Epic authorizing selection (read may populate either sub-record). */
function resolvedAuthorizingSelectionNpi(
  sel:
    | {
        authorizingProfessionalInfo?: { npi?: string | null }
        patientPhysician?: { npi?: string | null }
      }
    | null
    | undefined
): string {
  if (!sel) return ''
  const fromAuth = sel.authorizingProfessionalInfo?.npi?.trim() ?? ''
  if (fromAuth) return fromAuth
  return sel.patientPhysician?.npi?.trim() ?? ''
}

/**
 * Resolve the org-practitioner entry that corresponds to a delegation grantor (the
 * clinician an admin-configured delegate is acting on behalf of).
 *
 * The delegation record only carries the grantor's auth user id / name / email, while
 * org practitioners are keyed by IMDB practitioner_id. Match defensively in priority
 * order: by any user-id-like field (a true unique key, so first hit is safe), then by a
 * uniquely-matching email, then by a uniquely-matching normalized display name. Email and
 * name matches are only accepted when exactly one practitioner matches — an ambiguous
 * match (e.g. two clinicians with the same name) returns undefined so the UI falls back to
 * manual selection rather than silently attesting the wrong clinician.
 */
function findOrgPractitionerForGrantor(
  practitioners: OrganizationPractitionerEntry[],
  grantor: { grantorUserId: string; grantorName?: string; grantorEmail?: string } | undefined
): OrganizationPractitionerEntry | undefined {
  if (!grantor || practitioners.length === 0) return undefined

  const grantorId = grantor.grantorUserId?.trim().toLowerCase() || ''
  const grantorEmail = grantor.grantorEmail?.trim().toLowerCase() || ''
  const grantorName = grantor.grantorName?.trim().toLowerCase().replace(/\s+/g, ' ') || ''

  const userIdKeys = ['user_id', 'userId', 'auth0_id', 'auth0Id', 'sub', 'auth_user_id', 'authUserId']

  const dataUserIds = (entry: OrganizationPractitionerEntry): string[] => {
    const d = entry.data
    if (!d || typeof d !== 'object') return []
    return userIdKeys
      .map((k) => d[k])
      .filter((v): v is string => typeof v === 'string' && v.trim().length > 0)
      .map((v) => v.trim().toLowerCase())
  }

  const dataEmail = (entry: OrganizationPractitionerEntry): string => {
    const top = entry.email?.trim().toLowerCase() || ''
    if (top) return top
    const d = entry.data
    if (d && typeof d.email === 'string') return d.email.trim().toLowerCase()
    return ''
  }

  const normalizedName = (entry: OrganizationPractitionerEntry): string =>
    organizationPractitionerDisplayName(entry).trim().toLowerCase().replace(/\s+/g, ' ')

  // Accepts a predicate match only when it is unambiguous (exactly one practitioner),
  // so identity-by-attribute (email/name) can never silently pick the wrong clinician.
  const findUnique = (
    predicate: (entry: OrganizationPractitionerEntry) => boolean
  ): OrganizationPractitionerEntry | undefined => {
    const matches = practitioners.filter(predicate)
    return matches.length === 1 ? matches[0] : undefined
  }

  if (grantorId) {
    const byId = practitioners.find(
      (e) => e.practitioner_id.trim().toLowerCase() === grantorId || dataUserIds(e).includes(grantorId)
    )
    if (byId) return byId
  }

  if (grantorEmail) {
    const byEmail = findUnique((e) => dataEmail(e) === grantorEmail)
    if (byEmail) return byEmail
  }

  if (grantorName) {
    const byName = findUnique((e) => normalizedName(e) === grantorName)
    if (byName) return byName
  }

  return undefined
}

/**
 * Final review/attestation step for an order.
 *
 * End users land here after completing OrderStart. They can review the generated
 * requisition, choose whether they are submitting as themselves or on behalf of
 * another clinician, attest to the configured terms, and send the order to the lab.
 */
export function OrderReadyToSend() {
  const { config } = useUiConfig()
  /** Org-configured FHIR credential identifier system OID used to resolve a Practitioner's title. */
  const providerCredentialSystemId = config?.providerCredentialSystemId?.trim() || undefined
  /**
   * Normalized (uppercase, 2-letter-when-possible) US state of the organization from the client
   * config (``orgAddress.state``). Used to highlight authorizing-provider search results whose NPI
   * state matches the org's state. Empty string when not configured.
   */
  const orgState = useMemo(() => normalizeUsState(getTrfOrgAddress(config).state), [config])
  const {
    orgId,
    userId: currentUserId,
    roles = [],
    activeDelegation = null,
    availableDelegations = [],
  } = useAuthorization()
  const { mode: launchMode } = useLaunchMode()
  const location = useLocation()
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const { user: auth0User, logout: auth0Logout } = useAuth0()
  const [orderFormInfoData, setOrderFormInfoData] = useState<OrderFormInfoData | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState<string | null>(null)
  const [showSuccessModal, setShowSuccessModal] = useState(false)
  const [signatureRequestedCopy, setSignatureRequestedCopy] = useState<SignatureRequestedCopy | null>(null)
  /**
   * Set when the session submit API resolved but returned no orderId — the order may
   * have been created server-side but we cannot confirm or locate it. Puts the page
   * in a terminal/degraded state: CTA stays permanently disabled (no duplicate submit),
   * message rendered as an informational notice (not a retryable error).
   */
  const [submitDegradedNotice, setSubmitDegradedNotice] = useState<string | null>(null)
  /** True once the user has explicitly checked the attestation checkbox in the clinician card. */
  const [attestedTerms, setAttestedTerms] = useState(false)
  // Drives the post-submit "Print Order Form" action: opens the same
  // requisition PDF modal used by the in-progress page's "View Form" button so
  // the user can preview / print via the browser's native PDF viewer.
  const [isPrintFormModalOpen, setIsPrintFormModalOpen] = useState(false)
  // Drives the post-submit "Generate Shipping Label" action: requests a FedEx
  // kit label via POST /kits/shipping-label and previews it in ShippingLabelModal.
  const [isShippingLabelModalOpen, setIsShippingLabelModalOpen] = useState(false)
  const [shippingLabelState, setShippingLabelState] = useState<ShippingLabelModalState>({ status: 'idle' })
  const [submittedOrderId, setSubmittedOrderId] = useState<string | undefined>()
  const [submittedOrderData, setSubmittedOrderData] = useState<OrderFormInfoData | null>(null)
  const [submittingOnBehalfOf, setSubmittingOnBehalfOf] = useState<string>('')
  /** When user selects another provider from search (API returns full info). Null = submitting as current user. */
  const [selectedAuthorizingProvider, setSelectedAuthorizingProvider] = useState<{
    authorizingProfessionalInfo: AuthorizingProfessionalInfo
    patientPhysician: PhysicianInfo
  } | null>(null)
  const [searchResults, setSearchResults] = useState<
    { authorizingProfessionalInfo: AuthorizingProfessionalInfo; patientPhysician: PhysicianInfo }[]
  >([])
  const [isSearching, setIsSearching] = useState(false)
  const [showSearchDropdown, setShowSearchDropdown] = useState(false)
  const [submitOnBehalfOfAnother, setSubmitOnBehalfOfAnother] = useState(false)
  const submitOnBehalfRef = useRef(submitOnBehalfOfAnother)
  submitOnBehalfRef.current = submitOnBehalfOfAnother
  // In-flight attending (ATND) read; aborted when the user manually edits/picks an authorizing
  // clinician so a late-resolving auto-population can't clobber their choice.
  const attendingReadAbortRef = useRef<AbortController | null>(null)
  const [orgPractitionersWithNpi, setOrgPractitionersWithNpi] = useState<OrganizationPractitionerEntry[]>([])
  const [orgPractitionersLoading, setOrgPractitionersLoading] = useState(false)
  const [orgPractitionersError, setOrgPractitionersError] = useState<string | null>(null)
  const [standaloneOrgPractitionerId, setStandaloneOrgPractitionerId] = useState('')
  const [patientEncounters, setPatientEncounters] = useState<PatientEncounterOption[]>([])
  const [patientEncountersLoading, setPatientEncountersLoading] = useState(false)
  const [patientEncountersError, setPatientEncountersError] = useState<string | null>(null)
  const [selectedEncounterId, setSelectedEncounterId] = useState('')
  /** In-flight read of the encounter's attending practitioner (Epic on-behalf auto-population). */
  const [authorizingProviderLoading, setAuthorizingProviderLoading] = useState(false)
  /**
   * Fail-open net for the encounter/attending loading gates. `submitDisabled` blocks while an Epic
   * encounter search or attending-practitioner read is in flight, but that data is optional — a hung
   * request must never permanently disable Submit. When set (after a timeout), the loading flags no
   * longer contribute to `submitDisabled`, mirroring `coveragePendingTimedOut` for coverage.
   */
  const [loadingGateTimedOut, setLoadingGateTimedOut] = useState(false)
  const searchDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const searchAbortRef = useRef<AbortController | null>(null)
  const dropdownContainerRef = useRef<HTMLDivElement | null>(null)
  const blurRevertTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  /** Once true, we do not auto-pick the newest encounter again until the patient changes (user may clear optional selection). */
  const encounterAutofillAppliedRef = useRef(false)
  /** Tracks last Epic encounter + ATND id so we only auto-reset on-behalf state when the encounter (or its attending row) actually changes. */
  const prevEncounterKeyForBehalfRef = useRef<{ encId: string; attId: string | undefined } | null>(null)
  const hasHydratedSessionRef = useRef(false)
  /** Current user's own name parts / phone / medical title from GET /auth/me/profile. */
  const [selfProfile, setSelfProfile] = useState<SelfProfile | null>(null)
  /**
   * True once the GET /auth/me/profile fetch has settled (resolved, failed, or skipped for no
   * identity). The requisition preview defers its first PDF render until this flips true so the
   * TRF renders once with the provider's phone/roleTitle already merged, instead of rendering an
   * initial pass and re-rendering when the profile arrives.
   */
  const [selfProfileSettled, setSelfProfileSettled] = useState(false)
  /**
   * In-flight (or successfully-resolved) GET /auth/me/profile request. Kept in a ref so the
   * submit path can await the same fetch instead of racing the mount effect — this guarantees
   * the ordering provider's phone/roleTitle are populated before we build the canonical payload
   * (prevents the provider-api 000-000-0000 phone fallback on a fast submit). Only a successful
   * response is cached; failed/non-OK fetches clear the ref so a later call retries.
   */
  const selfProfilePromiseRef = useRef<Promise<SelfProfile | null> | null>(null)
  /** Auth identity the cached self-profile promise belongs to; guards against serving a stale user's data. */
  const selfProfileIdRef = useRef<string | null>(null)
  /**
   * Latest advisory coverage-check result from POST /ordering/sessions/{id}/coverage-check.
   * Null until the first check settles (or when the feature is off / the check fails — all
   * treated as "no warning", fail-open). Re-issued when the encounter/authorizing provider change.
   */
  const [coverageResult, setCoverageResult] = useState<CoverageCheckResponse | null>(null)
  /**
   * The composite `coverageSelectionKey` (session-synced selection snapshot + the authorizing NPI
   * sent in the request body) that the current `coverageResult` was computed against. `coverageResult`
   * is only trustworthy while this equals the live `coverageSelectionKey`; otherwise a re-check is
   * outstanding and coverage is treated as pending (see `coveragePending`), so a stale result can
   * neither allow nor block Submit for a different selection/authorizing clinician.
   */
  const [coverageCheckedKey, setCoverageCheckedKey] = useState<string | null>(null)
  /**
   * Safety net for the advisory coverage gate. `coveragePending` normally clears when the session
   * sync + coverage check settle for the current selection. But if the session sync never completes
   * (e.g. the self-profile fetch hangs so the debounced preview PATCH never runs, or the PATCH keeps
   * failing), pending could otherwise stay true forever — leaving Submit disabled and the "Checking
   * coverage eligibility…" spinner stuck. This flag, set by a bounded timer while pending persists,
   * forces the gate to fail-open so an advisory check can never permanently block submission.
   */
  const [coveragePendingTimedOut, setCoveragePendingTimedOut] = useState(false)

  const [searchParams, setSearchParams] = useSearchParams()
  const state = location.state as ReadyToSendState
  const epicState = typeof state === 'string' ? state : state?.epicState || ''
  const initialFormData = typeof state === 'string' ? null : state?.formData || null
  const initialOrderFormInfoData = typeof state === 'string' ? null : state?.orderFormInfoData || null
  const passedOrderId = typeof state === 'string' ? null : state?.orderId || null
  // Prefer navigation state session ID, fall back to URL param (survives F5 refresh)
  const passedSessionId =
    (typeof state === 'string' ? null : state?.sessionId || null) ?? searchParams.get('sessionId')
  const authFlow = typeof state === 'string' ? 'epic' : state?.authFlow || (epicState ? 'epic' : 'standalone')
  const isEpicFlow = authFlow === 'epic'

  const sessionPatchAbortRef = useRef<AbortController | null>(null)
  const sessionPatchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const cancelAutosave = useCallback(() => {
    if (sessionPatchTimerRef.current) {
      clearTimeout(sessionPatchTimerRef.current)
      sessionPatchTimerRef.current = null
    }
    sessionPatchAbortRef.current?.abort()
    sessionPatchAbortRef.current = null
  }, [])

  const handleSessionGoneRef = useRef<() => void>(() => {})

  const [draftSessionMeta, setDraftSessionMeta] = useState<{
    ownerUserId: string | null
    updatedAt: string | null
    patientLookupId: string | null
  }>({ ownerUserId: null, updatedAt: null, patientLookupId: null })
  const [draftSessionMetaLoaded, setDraftSessionMetaLoaded] = useState(() => !passedSessionId)

  // The session record supplies the patient id until the order form hydrates, so a
  // discard right after a refresh still writes the marker for the right patient.
  const patientLookupId = useMemo(
    () =>
      (orderFormInfoData
        ? resolvePatientIdForDraftLookup({
            launchMode,
            orderFormInfoData,
            epicOAuthState: epicState,
          })
        : null) ?? draftSessionMeta.patientLookupId,
    [draftSessionMeta.patientLookupId, launchMode, orderFormInfoData, epicState]
  )

  const clearLocalOrderContext = useCallback(() => {
    setOrderFormInfoData(null)
    setDraftSessionMeta({ ownerUserId: null, updatedAt: null, patientLookupId: null })
    setSearchParams({}, { replace: true })
    navigate(location.pathname, { replace: true, state: null })
  }, [location.pathname, navigate, setSearchParams])

  const patientDisplayName = orderFormInfoData?.patient?.name
    ? `${orderFormInfoData.patient.name.firstName ?? ''} ${orderFormInfoData.patient.name.lastName ?? ''}`.trim()
    : 'Patient'

  const {
    confirmOpen: discardConfirmOpen,
    isDiscarding,
    discardError,
    epicDiscarded,
    draftOwnerDisplayName,
    openDiscardConfirm,
    closeDiscardConfirm,
    performDiscard,
    handleSessionGone,
    clearDiscardError,
    confirmReady,
  } = useDiscardOrder({
    sessionId: passedSessionId,
    patientLookupId,
    authFlow: authFlow === 'epic' ? 'epic' : 'standalone',
    draftOwnerUserId: draftSessionMeta.ownerUserId,
    currentUserId,
    cancelAutosave,
    onClearLocalOrderContext: clearLocalOrderContext,
    draftOwnerMetadataPending: Boolean(passedSessionId) && !draftSessionMetaLoaded,
  })

  handleSessionGoneRef.current = handleSessionGone

  /**
   * Ordering-domain configVersion — read from navigation state (forwarded by OrderStart).
   * Falls back to the session record on page refresh (hydrateFromSession sets it below).
   * Used in the submit intent to avoid a hardcoded version string.
   */
  const navConfigVersion = typeof state === 'string' ? '' : state?.configVersion || ''
  const [configVersion, setConfigVersion] = useState<string>(navConfigVersion)
  /**
   * Submit catalog (code → consentFlowId) for the test currently selected on the order.
   * Sourced from the microapp ``UiConfig.testTypes`` (replaces the old
   * ``GET /config/ordering-domain`` ``tests[]`` array).
   */
  const orderingTestsFromNav: DomainTestLine[] = useMemo(
    () => (typeof state === 'object' && state && Array.isArray(state.orderingDomainTests) ? state.orderingDomainTests : []),
    [state]
  )
  const domainTestCatalog: DomainTestLine[] = useMemo(() => {
    const fromConfig = testLinesFromUiConfig(config)
    if (fromConfig.length > 0) return fromConfig
    return orderingTestsFromNav
  }, [config, orderingTestsFromNav])

  const { canonicalOrder: storedCanonical } = useAppSelector((s) => s.canonicalOrder)

  const effectiveConsentFlowForSessionSubmit = useMemo(
    () => resolveEffectiveConsentFlowForSubmit(config, storedCanonical?.order?.selectedOption ?? '', domainTestCatalog),
    [config, storedCanonical?.order?.selectedOption, domainTestCatalog]
  )
  // Legacy TRF formData — used only for the IMDB standalone-order submit path.
  // New session-based orders use storedCanonical directly.
  const formData: RequisitionFormData | null = initialFormData || null

  const providerConsent = config.providerConsent
  const providerConsentText = providerConsent?.providerConsentText ?? ''
  const submitOrderAttestationIntro =
    providerConsent?.submitOrderAttestationIntro?.trim() ||
    'By submitting this order, you attest that you have read and agree to the requisition form and terms below.'
  const clinicalAttestationTitle = providerConsent?.clinicalAttestationTitle ?? 'Clinical Attestation'
  const clinicalAttestationSummary = termsDisclosureSummary(
    providerConsent?.clinicalAttestationSummary,
    'Certifies that you are authorized to order medically necessary genetic testing, have obtained and documented all required patient and family consents, and agree to the accuracy of submitted information, applicable terms, and permitted communications and data sharing related to testing and research.'
  )
  const inflectionAuthorizationTitle =
    providerConsent?.inflectionAuthorizationTitle ?? 'Inflection Authorization'
  const inflectionAuthorizationSummary = termsDisclosureSummary(
    providerConsent?.inflectionAuthorizationSummary,
    'Confirms that you are an authorized ordering provider, permitted to use Inflection under institutional agreements, and that you designate Inflection to manage and transmit your order — including receiving and sharing order data, reports, and PHI with RCIGM — in accordance with applicable privacy and security regulations.'
  )
  const inflectionAuthorizationTerms =
    providerConsent?.inflectionAuthorizationTerms ??
    'You confirm that you are an authorized ordering provider for your institution and are permitted to use Inflection software and services under your organization’s agreements with Inflection. You designate Inflection to facilitate this order on your behalf, which may include receiving, processing, storing, and transmitting order information, results, and protected health information with Rady Children’s Institute for Genomic Medicine (RCIGM) and other designated partners as needed to perform and report testing, subject to applicable law, your institution’s policies, and Inflection’s agreements with you.'

  // Initial load path: use the patient/order data passed from OrderStart, or
  // reconstruct it from the Epic launch context for older Epic entry points.
  /** Clears encounter state when the page switches to a different patient. */
  useEffect(() => {
    if (initialOrderFormInfoData) {
      setOrderFormInfoData(initialOrderFormInfoData)
      return
    }
    if (!epicState) {
      return
    }
    /** Loads order context from Epic when this page is opened from an older Epic-only state shape. */
    const loadOrderInfo = async () => {
      try {
        const data = await extractFormOrderData(epicState)
        setOrderFormInfoData(data)
      } catch (error) {
        Sentry.captureException(error)
        setSubmitError(error instanceof Error ? error.message : 'Unable to load order details.')
      }
    }
    loadOrderInfo()
  }, [epicState, initialOrderFormInfoData])

  // ── Effect: hydrate canonical slice + page state from session on refresh ───────
  // This keeps /order/ready?sessionId=... usable after F5, when React Router
  // location.state has been lost but the backend ordering session still exists.
  /** Drops a selected encounter if a refreshed encounter list no longer contains it. */
  useEffect(() => {
    if (!passedSessionId || hasHydratedSessionRef.current) {
      return
    }
    if (initialFormData && initialOrderFormInfoData) {
      hasHydratedSessionRef.current = true
      return
    }
    hasHydratedSessionRef.current = true
    /** Restores canonical order and page header data from the persisted ordering session. */
    const hydrateFromSession = async () => {
      try {
        const providerClient = ProviderAPIClient.getInstance()
        const session = await providerClient.getOrderingSession(passedSessionId)
        setDraftSessionMeta({
          ownerUserId: typeof session['userId'] === 'string' ? session['userId'] : null,
          updatedAt: typeof session['updatedAt'] === 'string' ? session['updatedAt'] : null,
          patientLookupId: readSessionPatientLookupId(session),
        })
        const stateData = session['state'] as Record<string, unknown> | undefined
        const canonicalOrderData = stateData?.['canonicalOrder'] as CanonicalOrderPayload | undefined
        if (!canonicalOrderData) return
        dispatch(setCanonicalOrder(canonicalOrderData))
        // Restore configVersion from the session record so refresh does not lose it.
        const sessionCv = typeof session['configVersion'] === 'string' ? session['configVersion'] : ''
        if (sessionCv && !configVersion) setConfigVersion(sessionCv)
        if (!initialOrderFormInfoData) {
          const partial = mapCanonicalToOrderFormInfo(canonicalOrderData)
          setOrderFormInfoData(partial as OrderFormInfoData)
        }
        Sentry.logger.info('Review page hydrated from session on refresh', { sessionId: passedSessionId })
      } catch (error) {
        if (error instanceof OrderingSessionGoneError) {
          handleSessionGone()
          return
        }
        Sentry.captureException(error, { tags: { operation: 'order.review.session.hydrate' } })
        setSubmitError('Failed to restore order state. Please go back and try again.')
      }
    }
    hydrateFromSession()
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [passedSessionId])

  useEffect(() => {
    if (!passedSessionId) {
      setDraftSessionMeta({ ownerUserId: null, updatedAt: null, patientLookupId: null })
      setDraftSessionMetaLoaded(true)
      return
    }
    setDraftSessionMetaLoaded(false)
    let cancelled = false
    void ProviderAPIClient.getInstance()
      .getOrderingSession(passedSessionId)
      .then((session) => {
        if (cancelled) return
        setDraftSessionMeta({
          ownerUserId: typeof session['userId'] === 'string' ? session['userId'] : null,
          updatedAt: typeof session['updatedAt'] === 'string' ? session['updatedAt'] : null,
          patientLookupId: readSessionPatientLookupId(session),
        })
        setDraftSessionMetaLoaded(true)
      })
      .catch((err) => {
        if (cancelled) return
        if (err instanceof OrderingSessionGoneError) {
          handleSessionGoneRef.current()
        }
        setDraftSessionMetaLoaded(true)
      })
    return () => {
      cancelled = true
    }
  }, [passedSessionId])

  useEffect(() => {
    setSelectedEncounterId('')
    encounterAutofillAppliedRef.current = false
    prevEncounterKeyForBehalfRef.current = null
  }, [orderFormInfoData?.patient?.id])

  /** Epic: load Encounter?patient= for dropdown on ready-to-submit screen. */
  useEffect(() => {
    if (!isEpicFlow || !epicState || !orderFormInfoData?.patient?.id?.trim()) {
      setPatientEncounters([])
      setPatientEncountersLoading(false)
      setPatientEncountersError(null)
      return
    }
    const ac = new AbortController()
    setPatientEncountersLoading(true)
    setPatientEncountersError(null)
    setPatientEncounters([])
    searchPatientEncounters(epicState, orderFormInfoData.patient.id, ac.signal)
      .then((list) => {
        if (!ac.signal.aborted) {
          setPatientEncounters(list)
          if (list.length > 0 && !encounterAutofillAppliedRef.current) {
            encounterAutofillAppliedRef.current = true
            const contextCsn = EpicAuthClient.getInstance().getContext(epicState).encounterCsn
            setSelectedEncounterId(selectDefaultEncounterId(list, contextCsn))
          }
        }
      })
      .catch((err: unknown) => {
        if (err instanceof DOMException && err.name === 'AbortError') return
        if (!ac.signal.aborted) {
          setPatientEncounters([])
          setPatientEncountersError(err instanceof Error ? err.message : 'Unable to load encounters.')
        }
      })
      .finally(() => {
        if (!ac.signal.aborted) {
          setPatientEncountersLoading(false)
        }
      })
    return () => ac.abort()
  }, [isEpicFlow, epicState, orderFormInfoData?.patient?.id])

  useEffect(() => {
    if (!selectedEncounterId) return
    if (patientEncounters.some((e) => e.id === selectedEncounterId)) return
    setSelectedEncounterId('')
  }, [patientEncounters, selectedEncounterId])

  const selectedPatientEncounter = useMemo(() => {
    if (!selectedEncounterId) return undefined
    return patientEncounters.find((e) => e.id === selectedEncounterId)
  }, [patientEncounters, selectedEncounterId])

  /** Single source of current-user auth (Epic context or Auth0). One getContext / one claims read; all downstream values derived here. */
  const currentUserAuth = useMemo((): {
    name: string
    firstName: string
    lastName: string
    /** True when the parts were guessed by splitting a display name rather than read as fields. */
    namePartsAreDerived: boolean
    npi: string
    email: string
    credentials: string
    phone: string
    id: string | null
  } => {
    const npiClaim =
      (auth0User as { [k: string]: unknown })?.['https://inflectionmedicine.com/npi'] ??
      (auth0User as { [k: string]: unknown })?.['https://provider.dev.inflectionmedicine.com/npi']
    const auth0Npi = typeof npiClaim === 'string' ? npiClaim : ''
    const credentialsClaim =
      (auth0User as { [k: string]: unknown })?.['https://inflectionmedicine.com/credentials'] ??
      (auth0User as { [k: string]: unknown })?.['https://provider.dev.inflectionmedicine.com/credentials']
    const auth0Credentials = typeof credentialsClaim === 'string' ? credentialsClaim : ''
    const phoneClaim =
      (auth0User as { [k: string]: unknown })?.['https://inflectionmedicine.com/phone'] ??
      (auth0User as { [k: string]: unknown })?.['https://provider.dev.inflectionmedicine.com/phone']
    const auth0Phone = typeof phoneClaim === 'string' ? phoneClaim : ''
    if (isEpicFlow && epicState) {
      const context = EpicAuthClient.getInstance().getContext(epicState)
      const firstName = context.userName ?? ''
      const lastName = context.userLastName ?? ''
      const name = `${firstName} ${lastName}`.trim()
      return {
        name,
        firstName,
        lastName,
        namePartsAreDerived: false,
        npi: context.providerNpi ?? '',
        email: context.userEmail ?? '',
        credentials: auth0Credentials,
        phone: auth0Phone,
        id:
          context.epicUserFhirId ||
          context.epicUserId ||
          context.fhirUserId ||
          'epic-current-user',
      }
    }
    if (!auth0User) {
      return {
        name: '',
        firstName: '',
        lastName: '',
        namePartsAreDerived: false,
        npi: auth0Npi,
        email: '',
        credentials: auth0Credentials,
        phone: auth0Phone,
        id: null,
      }
    }
    const name =
      auth0User.name ||
      `${(auth0User as { given_name?: string }).given_name || ''} ${(auth0User as { family_name?: string }).family_name || ''}`.trim()
    const parts = (typeof name === 'string' ? name : '').split(/\s+/).filter(Boolean)
    const firstName = parts[0] ?? ''
    const lastName = parts.length > 1 ? parts.slice(1).join(' ') : ''
    return {
      name: typeof name === 'string' ? name : '',
      firstName,
      lastName,
      namePartsAreDerived: true,
      npi: auth0Npi,
      email: (auth0User as { email?: string }).email ?? '',
      credentials: auth0Credentials,
      phone: auth0Phone,
      id: (auth0User as { sub?: string }).sub ?? 'auth0-current-user',
    }
  }, [isEpicFlow, epicState, auth0User])

  const currentUserPhysicianName = currentUserAuth.name
  const currentUserPhysicianNpi = currentUserAuth.npi
  const currentUserHasNpi = Boolean(currentUserAuth.npi?.trim())

  // Phone and medical title (roleTitle) are not present in Auth0 claims or the Epic
  // context, so fetch the current user's own profile to populate Section C / D on the
  // TRF when the logged-in user is the ordering provider (GEN: TRF phone/roleTitle).
  //
  // In the Epic flow, the medical title (roleTitle) is instead resolved from Epic via the FHIR
  // Practitioner read (POST /fhir/practitioner/read/{id}) on the current user's FHIR id, using the
  // org's configured credential system OID — so the title matches the source EHR data and the
  // config the search path uses. Phone/name still come from /auth/me/profile (Epic lacks them).
  const fetchSelfProfile = useCallback((): Promise<SelfProfile | null> => {
    const id = currentUserAuth.id
    if (!id) {
      setSelfProfileSettled(true)
      return Promise.resolve(null)
    }
    // Discard any promise cached for a different identity so we never serve a prior
    // user's phone/title (e.g. after an account switch on the same mounted page).
    if (selfProfileIdRef.current !== id) {
      selfProfilePromiseRef.current = null
      selfProfileIdRef.current = id
      setSelfProfile(null)
      setSelfProfileSettled(false)
    }
    if (selfProfilePromiseRef.current) return selfProfilePromiseRef.current
    const promise = (async (): Promise<SelfProfile | null> => {
      // Epic flow: the credential/title comes from the EHR (config-OID resolved) via the FHIR
      // Practitioner read on the current user's FHIR id. Resolved up front and independently of
      // /auth/me/profile so the credential still surfaces even if the profile fetch fails.
      let epicRoleTitle = ''
      if (isEpicFlow && epicState) {
        const epicUserFhirId = EpicAuthClient.getInstance().getContext(epicState).epicUserFhirId?.trim()
        if (epicUserFhirId) {
          try {
            const selfPractitioner = await readPractitionerById(
              epicState,
              epicUserFhirId,
              undefined,
              providerCredentialSystemId
            )
            epicRoleTitle = selfPractitioner?.patientPhysician.roleTitle?.trim() ?? ''
          } catch (e) {
            // Non-fatal: fall back to the /auth/me/profile roleTitle below.
            Sentry.captureException(e, { tags: { operation: 'order.review.self.epic.practitioner.read' } })
          }
        }
      }
      try {
        const providerClient = ProviderAPIClient.getInstance()
        const response = await providerClient.get('/auth/me/profile')
        if (!response.ok) {
          // Non-OK: do not cache the failure — allow a later call (e.g. submit) to retry.
          // Still surface whatever the EHR provided (title) so the credential isn't lost.
          if (selfProfileIdRef.current === id) {
            selfProfilePromiseRef.current = null
            if (epicRoleTitle) {
              const ehrOnly = { phone: '', roleTitle: epicRoleTitle, firstName: '', lastName: '' }
              setSelfProfile(ehrOnly)
              return ehrOnly
            }
          }
          return null
        }
        const data = (await response.json()) as {
          phone?: string | null
          roleTitle?: string | null
          firstName?: string | null
          lastName?: string | null
        }
        // Epic-resolved credential wins over the profile's roleTitle; fall back to the profile
        // value when the EHR read yielded nothing.
        const roleTitle = epicRoleTitle || (data.roleTitle ?? '').trim()
        const next = {
          phone: (data.phone ?? '').trim(),
          roleTitle,
          firstName: (data.firstName ?? '').trim(),
          lastName: (data.lastName ?? '').trim(),
        }
        // Ignore a stale response if the identity changed while in flight.
        if (selfProfileIdRef.current === id) setSelfProfile(next)
        return next
      } catch (e) {
        Sentry.captureException(e, { tags: { operation: 'order.review.me.profile.get' } })
        // Clear the ref so a later retry (e.g. at submit) can re-attempt the fetch.
        // Still surface the EHR-resolved credential (title) so a thrown request / JSON parse
        // error does not blank the current user's credential.
        if (selfProfileIdRef.current === id) {
          selfProfilePromiseRef.current = null
          if (epicRoleTitle) {
            const ehrOnly = { phone: '', roleTitle: epicRoleTitle, firstName: '', lastName: '' }
            setSelfProfile(ehrOnly)
            return ehrOnly
          }
        }
        return null
      } finally {
        // Unblock the requisition preview once the fetch settles (success or failure), so the
        // TRF renders with whatever provider data is available rather than waiting indefinitely.
        if (selfProfileIdRef.current === id) setSelfProfileSettled(true)
      }
    })()
    selfProfilePromiseRef.current = promise
    return promise
  }, [currentUserAuth.id, isEpicFlow, epicState, providerCredentialSystemId])

  useEffect(() => {
    void fetchSelfProfile()
  }, [fetchSelfProfile])

  /** Converts the authenticated user into the provider shape expected by order payloads. */
  const currentUserAsOrderingProvider = useMemo((): PhysicianInfo | null => {
    if (currentUserAuth.id == null) return null
    // Prefer the current user's own profile values (phone / roleTitle from GET
    // /auth/me/profile, credentials from Auth0 claims). Fall back to any values
    // already on the ordering provider (sourced from FHIR/DB) rather than blanking
    // them (GEN: TRF phone/credentials/roleTitle).
    const existing = orderFormInfoData?.orderingProvider
    const phone = selfProfile?.phone?.trim() || currentUserAuth.phone?.trim() || existing?.phone?.trim() || ''
    const roleTitle = selfProfile?.roleTitle?.trim() || existing?.roleTitle?.trim() || ''
    const credentials = currentUserAuth.credentials?.trim() || existing?.credentials?.trim() || ''
    // Only override parts that were guessed by splitting a display name — that split folds any
    // middle name into the surname, so it reaches the TRF as part of the last name (GEN-3940).
    // Epic supplies real fields, so it is left alone. Each part falls back independently so a
    // practitioner with only one part recorded keeps the other from the split.
    const useStoredParts = currentUserAuth.namePartsAreDerived
    const firstName = (useStoredParts && selfProfile?.firstName?.trim()) || currentUserAuth.firstName
    const lastName = (useStoredParts && selfProfile?.lastName?.trim()) || currentUserAuth.lastName
    return {
      id: currentUserAuth.id,
      name: { firstName, lastName },
      roleTitle: roleTitle || '',
      phone,
      email: currentUserAuth.email,
      npi: currentUserAuth.npi?.trim() ? currentUserAuth.npi : undefined,
      credentials: credentials || undefined,
    }
  }, [currentUserAuth, orderFormInfoData?.orderingProvider, selfProfile])

  const providerName =
    currentUserAuth.name || orderFormInfoData?.medicalProfessionalInfo?.name || ''

  /**
   * Epic + encounter: drive "Select Authorizing Clinician" from encounter attending vs
   * current-user NPI (checked when ATND is present; unchecked when no ATND but the user
   * has an NPI so they can self-attest; no-NPI + no ATND starts unchecked until the user opts in).
   *
   * useLayoutEffect so ``submitOnBehalfOfAnother`` is true in the same commit cycle before
   * passive effects (e.g. attending practitioner read) run; plain useEffect would leave
   * ``submitOnBehalfOfAnother`` stale for sibling effects.
   */
  useLayoutEffect(() => {
    if (!isEpicFlow || !orderFormInfoData) return
    if (!selectedEncounterId) return

    const prevKey = prevEncounterKeyForBehalfRef.current
    const encounterChanged = !prevKey || prevKey.encId !== selectedEncounterId
    const attendingBecameAbsent =
      !encounterChanged &&
      Boolean(prevKey.attId) &&
      !selectedPatientEncounter?.attendingPractitionerId

    const hasAttending = Boolean(selectedPatientEncounter?.attendingPractitionerId)

    if (hasAttending) {
      setSubmitOnBehalfOfAnother(true)
      prevEncounterKeyForBehalfRef.current = {
        encId: selectedEncounterId,
        attId: selectedPatientEncounter?.attendingPractitionerId,
      }
      return
    }

    if (currentUserHasNpi) {
      if (encounterChanged || attendingBecameAbsent) {
        setSubmitOnBehalfOfAnother(false)
        setSelectedAuthorizingProvider(null)
        setSubmittingOnBehalfOf(providerName)
        setStandaloneOrgPractitionerId('')
        setSearchResults([])
        setShowSearchDropdown(false)
      }
      prevEncounterKeyForBehalfRef.current = { encId: selectedEncounterId, attId: undefined }
      return
    }

    if (encounterChanged) {
      setSubmitOnBehalfOfAnother(false)
      setSelectedAuthorizingProvider(null)
      setSubmittingOnBehalfOf('')
      setStandaloneOrgPractitionerId('')
      setSearchResults([])
      setShowSearchDropdown(false)
    }
    prevEncounterKeyForBehalfRef.current = { encId: selectedEncounterId, attId: undefined }
  }, [
    isEpicFlow,
    orderFormInfoData,
    selectedEncounterId,
    selectedPatientEncounter?.attendingPractitionerId,
    currentUserHasNpi,
    providerName,
  ])

  /**
   * Epic-only: when an Encounter is selected, attempt to auto-populate the
   * authorizing provider from the encounter's attending (ATND) participant.
   * Runs after the encounter/on-behalf sync layout effect so ``submitOnBehalfOfAnother``
   * is already true when an ATND row is present.
   */
  useEffect(() => {
    if (!isEpicFlow || !epicState || !selectedEncounterId) return
    setSelectedAuthorizingProvider(null)
    setSubmittingOnBehalfOf(selectedPatientEncounter?.attendingPractitionerDisplay?.trim() ?? '')
    setSearchResults([])
    setShowSearchDropdown(false)
    if (!selectedPatientEncounter?.attendingPractitionerId) return
    if (!submitOnBehalfOfAnother) return

    const practitionerId = selectedPatientEncounter.attendingPractitionerId
    let cancelled = false
    const ac = new AbortController()
    attendingReadAbortRef.current = ac
    setAuthorizingProviderLoading(true)

    const resolve = async () => {
      try {
        const result = await readPractitionerById(epicState, practitionerId, ac.signal, providerCredentialSystemId)
        // The search field stays editable during the read. If the user manually picked or typed an
        // authorizing clinician while this was in flight, that interaction aborts ``ac`` so we never
        // reach here and clobber their choice.
        //
        // Apply the resolved attending even when it has no NPI: this surfaces the prefilled
        // provider's title/name from the encounter for display. Submission stays gated on an
        // NPI-bearing selection via `resolvedAuthorizingSelectionNpi` (see
        // `epicManualAuthorizingIncomplete` / `missingNpiNeedsAuthorizing`), so a title-only
        // attending cannot be submitted without the user supplying an NPI-bearing clinician.
        if (!cancelled && !ac.signal.aborted && result && submitOnBehalfRef.current) {
          setSelectedAuthorizingProvider(result)
          setSubmittingOnBehalfOf(result.authorizingProfessionalInfo.name)
          setSearchResults([])
          setShowSearchDropdown(false)
        }
      } catch {
        // Silently fall back to the manual practitioner search flow
      } finally {
        if (!cancelled && !ac.signal.aborted) setAuthorizingProviderLoading(false)
      }
    }

    void resolve()
    return () => {
      cancelled = true
      ac.abort()
      setAuthorizingProviderLoading(false)
      if (attendingReadAbortRef.current === ac) {
        attendingReadAbortRef.current = null
      }
    }
  }, [isEpicFlow, epicState, selectedEncounterId, selectedPatientEncounter, submitOnBehalfOfAnother, providerCredentialSystemId])


  /** Defaults the authorizing display name to the current user outside on-behalf-of mode. */
  useEffect(() => {
    if (providerName && !submitOnBehalfOfAnother && !(isEpicFlow && selectedEncounterId)) {
      setSubmittingOnBehalfOf((prev) => (prev === '' ? providerName : prev))
    }
  }, [providerName, submitOnBehalfOfAnother, isEpicFlow, selectedEncounterId])

  /** Clears a stale Epic search selection when the input matches the logged-in user.
   *  Skip when providerName is blank: Change empties the search box, and '' === '' would
   *  wipe a real attending for users who have no display name. */
  useEffect(() => {
    if (
      !isEpicFlow ||
      selectedAuthorizingProvider == null ||
      !providerName.trim() ||
      submittingOnBehalfOf.trim() !== providerName ||
      selectedAuthorizingProvider.authorizingProfessionalInfo.name === providerName
    ) {
      return
    }
    setSelectedAuthorizingProvider(null)
  }, [isEpicFlow, providerName, submittingOnBehalfOf, selectedAuthorizingProvider])

  /**
   * Aborts any in-flight attending-practitioner read and clears its loading flag. The read's
   * own cleanup only clears the flag on unmount / dependency change, and its finally-block skips
   * the clear when the signal was aborted — so any manual abort (user typing a search query or
   * picking a clinician) must clear the flag here or `submitDisabled` would stay locked forever.
   */
  const abortAttendingRead = useCallback(() => {
    attendingReadAbortRef.current?.abort()
    setAuthorizingProviderLoading(false)
  }, [])

  /** Call when user selects a provider from search; populates authorizingProfessionalInfo and patientPhysician on submit. */
  const handleAuthorizingProviderSelect = useCallback(
    (result: {
      authorizingProfessionalInfo: AuthorizingProfessionalInfo
      patientPhysician: PhysicianInfo
    }) => {
      if (blurRevertTimeoutRef.current) {
        clearTimeout(blurRevertTimeoutRef.current)
        blurRevertTimeoutRef.current = null
      }
      abortAttendingRead()
      setSelectedAuthorizingProvider(result)
      setSubmittingOnBehalfOf(result.authorizingProfessionalInfo.name)
      setShowSearchDropdown(false)
      setSearchResults([])
    },
    [abortAttendingRead]
  )

  /** Resets the authorizing clinician picker back to the logged-in ordering user. */
  const handleSelectCurrentUser = useCallback(() => {
    if (blurRevertTimeoutRef.current) {
      clearTimeout(blurRevertTimeoutRef.current)
      blurRevertTimeoutRef.current = null
    }
    abortAttendingRead()
    setSelectedAuthorizingProvider(null)
    setSubmittingOnBehalfOf(providerName)
    setStandaloneOrgPractitionerId('')
    setShowSearchDropdown(false)
    setSearchResults([])
    setSubmitOnBehalfOfAnother(false)
  }, [providerName, abortAttendingRead])

  /** Alphabetizes organization practitioners so the standalone picker is stable and scannable. */
  const sortedOrgPractitioners = useMemo(
    () =>
      [...orgPractitionersWithNpi].sort((a, b) =>
        organizationPractitionerDisplayName(a).localeCompare(organizationPractitionerDisplayName(b))
      ),
    [orgPractitionersWithNpi]
  )

  /** Web app: load org practitioners (JWT org must match path). */
  useEffect(() => {
    if (isEpicFlow || !orgId) {
      setOrgPractitionersWithNpi([])
      setOrgPractitionersError(null)
      setOrgPractitionersLoading(false)
      return
    }
    const ac = new AbortController()
    setOrgPractitionersLoading(true)
    setOrgPractitionersError(null)
    getOrganizationPractitioners(orgId, ac.signal)
      .then((list) => {
        const filtered = filterOrganizationPractitionersWithNpi(list)
        setOrgPractitionersWithNpi(dedupeOrganizationPractitionersByPractitionerId(filtered))
      })
      .catch((err: unknown) => {
        if (err instanceof DOMException && err.name === 'AbortError') return
        const msg = err instanceof Error ? err.message : 'Failed to load practitioners.'
        setOrgPractitionersError(msg)
        setOrgPractitionersWithNpi([])
        Sentry.captureException(err instanceof Error ? err : new Error(String(err)))
      })
      .finally(() => {
        if (!ac.signal.aborted) {
          setOrgPractitionersLoading(false)
        }
      })
    return () => ac.abort()
  }, [isEpicFlow, orgId])

  /**
   * Web standalone: when the signed-in user is an admin-configured delegate, resolve the
   * clinician they act on behalf of and auto-select that grantor as the authorizing
   * clinician. This makes the delegate the ordering provider and the grantor the
   * authorizing provider/attestor; without it the delegate's own identity is used.
   *
   * The grantor is taken from the explicitly chosen "Acting as:" delegation when set,
   * otherwise from the sole active delegation (the common admin case where a delegate is
   * tied to exactly one clinician and never opens the selector). Applies once per resolved
   * grantor so a later manual change by the user is preserved.
   */
  const appliedDelegationGrantorRef = useRef<string | null>(null)
  const isDelegateRole = roles.includes('delegate')
  /** Active (status) delegations granted to the current user. */
  const activeGrantedDelegations = useMemo(
    () => availableDelegations.filter((d) => d.status === 'active'),
    [availableDelegations]
  )
  /**
   * The grantor user id to act as: the explicit "Acting as:" selection when present,
   * else the only active delegation (unambiguous admin-configured delegate).
   */
  const effectiveDelegationGrantorId = useMemo((): string | null => {
    if (activeDelegation) return activeDelegation
    if (activeGrantedDelegations.length === 1) return activeGrantedDelegations[0].grantorUserId
    return null
  }, [activeDelegation, activeGrantedDelegations])
  useEffect(() => {
    if (isEpicFlow || !isDelegateRole) return
    if (!effectiveDelegationGrantorId) {
      appliedDelegationGrantorRef.current = null
      return
    }
    if (orgPractitionersLoading || sortedOrgPractitioners.length === 0) return
    if (appliedDelegationGrantorRef.current === effectiveDelegationGrantorId) return

    const grantor = availableDelegations.find((d) => d.grantorUserId === effectiveDelegationGrantorId)
    const entry = findOrgPractitionerForGrantor(sortedOrgPractitioners, grantor)
    if (!entry) {
      // No confident match: leave manual selection in place but mark as resolved so we
      // do not re-run on every render for the same delegation. Capture a diagnostic so we
      // can see why a delegate's grantor failed to map to an org practitioner.
      appliedDelegationGrantorRef.current = effectiveDelegationGrantorId
      Sentry.captureMessage('Delegate grantor did not match an org practitioner', {
        level: 'warning',
        extra: {
          grantorUserId: effectiveDelegationGrantorId,
          grantorName: grantor?.grantorName,
          grantorEmail: grantor?.grantorEmail,
          orgPractitionerSample: sortedOrgPractitioners.slice(0, 10).map((p) => ({
            practitioner_id: p.practitioner_id,
            email: p.email,
            dataKeys: p.data ? Object.keys(p.data) : [],
          })),
        },
      })
      return
    }

    const sel = organizationPractitionerToAuthorizingSelection(entry)
    appliedDelegationGrantorRef.current = effectiveDelegationGrantorId
    setStandaloneOrgPractitionerId(entry.practitioner_id)
    setSelectedAuthorizingProvider(sel)
    setSubmitOnBehalfOfAnother(true)
    setSubmittingOnBehalfOf(sel.authorizingProfessionalInfo.name)
  }, [
    isEpicFlow,
    isDelegateRole,
    effectiveDelegationGrantorId,
    availableDelegations,
    sortedOrgPractitioners,
    orgPractitionersLoading,
  ])

  /** Debounces Epic practitioner search while the user types an authorizing clinician name. */
  useEffect(() => {
    if (authFlow !== 'epic' || !epicState) return
    const query = submittingOnBehalfOf.trim()
    if (query.length < 2) {
      setSearchResults([])
      setIsSearching(false)
      return
    }
    // Skip search when text exactly matches the selected provider (user just picked from dropdown)
    if (
      selectedAuthorizingProvider &&
      query === selectedAuthorizingProvider.authorizingProfessionalInfo.name
    ) {
      if (searchDebounceRef.current) {
        clearTimeout(searchDebounceRef.current)
        searchDebounceRef.current = null
      }
      setSearchResults([])
      setIsSearching(false)
      return
    }
    if (searchDebounceRef.current) {
      clearTimeout(searchDebounceRef.current)
      searchDebounceRef.current = null
    }
    setIsSearching(true)
    searchDebounceRef.current = setTimeout(async () => {
      searchDebounceRef.current = null
      if (searchAbortRef.current) {
        searchAbortRef.current.abort()
      }
      const controller = new AbortController()
      searchAbortRef.current = controller
      setSearchResults([])
      try {
        const results = await searchPractitioners(epicState, query, controller.signal)
        setSearchResults(results)
      } catch {
        setSearchResults([])
      } finally {
        // Only clear if this request is still current (no newer search started)
        if (searchAbortRef.current === controller) {
          searchAbortRef.current = null
          setIsSearching(false)
        }
      }
    }, 500)
    return () => {
      if (searchDebounceRef.current) {
        clearTimeout(searchDebounceRef.current)
        searchDebounceRef.current = null
      }
      if (searchAbortRef.current) {
        searchAbortRef.current.abort()
        searchAbortRef.current = null
      }
      setIsSearching(false)
    }
  }, [
    authFlow,
    isEpicFlow,
    epicState,
    submittingOnBehalfOf,
    selectedAuthorizingProvider,
  ])

  /** Close the search dropdown (e.g. on blur or click outside). Does not change the input value. */
  const closeSearchDropdown = useCallback(() => {
    if (blurRevertTimeoutRef.current) {
      clearTimeout(blurRevertTimeoutRef.current)
      blurRevertTimeoutRef.current = null
    }
    setShowSearchDropdown(false)
  }, [])

  /** Registers the document-level click handler that dismisses the clinician search dropdown. */
  useEffect(() => {
    /** Closes the authorizing clinician dropdown when focus/click moves outside it. */
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownContainerRef.current && !dropdownContainerRef.current.contains(e.target as Node)) {
        closeSearchDropdown()
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [closeSearchDropdown])

  const providerNpi = orderFormInfoData?.medicalProfessionalInfo?.npi || ''

  /** Reset Epic authorizing state when the encounter is cleared (preserve existing no-encounter behavior). */
  useEffect(() => {
    if (!isEpicFlow || !orderFormInfoData || selectedEncounterId) return
    prevEncounterKeyForBehalfRef.current = null
    setSubmitOnBehalfOfAnother(false)
    setSelectedAuthorizingProvider(null)
    setStandaloneOrgPractitionerId('')
    setSearchResults([])
    setShowSearchDropdown(false)
    if (currentUserHasNpi) {
      setSubmittingOnBehalfOf(providerName)
    } else {
      setSubmittingOnBehalfOf('')
    }
  }, [isEpicFlow, selectedEncounterId, orderFormInfoData, currentUserHasNpi, providerName])

  /** Authorizing provider name and NPI (selected from search or current user); used for consent. */
  const { authorizingProviderName, authorizingProviderNpi } = useMemo(() => {
    const info =
      selectedAuthorizingProvider?.authorizingProfessionalInfo ?? orderFormInfoData?.medicalProfessionalInfo
    const resolvedSelNpi = resolvedAuthorizingSelectionNpi(selectedAuthorizingProvider)
    // When an authorizing provider is explicitly selected, its NPI is the ONLY
    // valid source — if that clinician has no NPI on file the value must be
    // empty, NOT the ordering provider's NPI (`providerNpi`). Falling back to
    // `providerNpi` here surfaced the ordering provider's NPI for a selected
    // authorizing provider who lacks one. The `providerNpi` fallback only
    // applies to the default (no-selection) case, where `info` already IS the
    // ordering/medical professional record.
    const resolvedNpi = selectedAuthorizingProvider
      ? resolvedSelNpi || info?.npi || ''
      : resolvedSelNpi || info?.npi || providerNpi || ''
    return {
      authorizingProviderName: info?.name || providerName || '',
      authorizingProviderNpi: resolvedNpi.trim(),
    }
  }, [selectedAuthorizingProvider, orderFormInfoData?.medicalProfessionalInfo, providerName, providerNpi])

  /**
   * NPI of the clinician who authorizes this order — the selected authorizing provider when
   * submitting on behalf of another, otherwise the logged-in user. This is the single source of
   * truth for both the attestation (`attestorNpi`) and the coverage check's `provider` dimension
   * (the server no longer derives it, so the UI must send it). Empty string when unknown.
   */
  const authorizingNpiForCoverage = useMemo(() => {
    const onBehalf = selectedAuthorizingProvider != null
    return ((onBehalf ? authorizingProviderNpi : currentUserPhysicianNpi) || '').trim()
  }, [selectedAuthorizingProvider, authorizingProviderNpi, currentUserPhysicianNpi])

  /** Computes the audit caller name used by legacy IMDB order operations. */
  const getCallerName = (data?: OrderFormInfoData | null) => {
    if (epicState) {
      const accessContext = EpicAuthClient.getInstance().getContext(epicState)
      const epicName = `${accessContext.userName || ''} ${accessContext.userLastName || ''}`.trim()
      if (epicName) {
        return epicName
      }
    }
    const providerNameFallback = data?.orderingProvider?.name
      ? `${data.orderingProvider.name.firstName || ''} ${data.orderingProvider.name.lastName || ''}`.trim()
      : ''
    return providerNameFallback || 'spa'
  }

  /** Builds the legacy consent form payload from the chosen attestor and configured consent text. */
  const buildConsentFormData = (currentFormData: RequisitionFormData) => {
    const signatureDate = getLocalCalendarDateString()
    return {
      ...DEFAULT_CONSENT_FORM_DATA,
      ...currentFormData.consentFormData,
      medicalProfessionalName:
        currentFormData.consentFormData?.medicalProfessionalName ||
        authorizingProviderName ||
        '',
      npi: currentFormData.consentFormData?.npi || authorizingProviderNpi || '',
      signatureDate: currentFormData.consentFormData?.signatureDate || signatureDate,
      signature: currentFormData.consentFormData?.signature || authorizingProviderName || 'Signed electronically',
      consentedText:
        providerConsentText ||
        currentFormData.consentFormData?.consentedText ||
        DEFAULT_CONSENT_FORM_DATA.consentedText,
    }
  }

  /** Maps the selected Epic encounter option into the compact payload shape used by submissions. */
  const selectedEpicEncounter = useMemo(() => {
    if (!selectedEncounterId) return undefined
    const opt = patientEncounters.find((e) => e.id === selectedEncounterId)
    if (!opt) return undefined
    return { id: opt.id, summary: opt.label }
  }, [selectedEncounterId, patientEncounters])

  /**
   * Build the order identity shown in the preview and sent on submit.
   * orderingProvider is the logged-in submitter; authorizingProfessionalInfo is
   * populated only when the user chooses to submit on behalf of another clinician.
   */
  const orderFormInfoDataWithAuthorizing = useMemo((): OrderFormInfoData | null => {
    if (!orderFormInfoData) return null
    const authorizingForPatientPhysicianNpi =
      selectedAuthorizingProvider?.authorizingProfessionalInfo ?? orderFormInfoData.medicalProfessionalInfo
    const orderingProvider = currentUserAsOrderingProvider ?? orderFormInfoData.orderingProvider ?? undefined
    // Self-submit falls back to the resolved ordering provider rather than the raw form value,
    // which may still hold parts guessed by splitting a display name (GEN-3940). Matches the
    // authorizingProvider fallback below, so all three self-referring blocks agree.
    const rawPatientPhysician =
      selectedAuthorizingProvider?.patientPhysician ?? orderingProvider ?? orderFormInfoData.orderingProvider
    const patientPhysician =
      rawPatientPhysician != null
        ? {
            ...rawPatientPhysician,
            npi:
              rawPatientPhysician.npi ??
              authorizingForPatientPhysicianNpi.npi ??
              orderFormInfoData.medicalProfessionalInfo?.npi ??
              '',
          }
        : undefined

    const merged: OrderFormInfoData = {
      ...orderFormInfoData,
      ...(orderingProvider != null && { orderingProvider }),
      ...(patientPhysician != null && { patientPhysician }),
      ...(selectedEpicEncounter != null && { epicEncounter: selectedEpicEncounter }),
    }

    if (selectedAuthorizingProvider) {
      merged.authorizingProfessionalInfo = { ...selectedAuthorizingProvider.authorizingProfessionalInfo }
      merged.authorizingProvider =
        patientPhysician != null
          ? { ...patientPhysician }
          : {
              ...selectedAuthorizingProvider.patientPhysician,
              npi:
                selectedAuthorizingProvider.patientPhysician.npi ??
                selectedAuthorizingProvider.authorizingProfessionalInfo.npi ??
                '',
            }
    } else {
      delete merged.authorizingProfessionalInfo
      const selfAuthSource = orderingProvider ?? orderFormInfoData.orderingProvider
      if (selfAuthSource != null) {
        merged.authorizingProvider = { ...selfAuthSource }
      } else {
        delete merged.authorizingProvider
      }
    }

    return merged
  }, [orderFormInfoData, selectedAuthorizingProvider, currentUserAsOrderingProvider, selectedEpicEncounter])

  /**
   * Canonical payload used to drive the **server-side** TRF preview (GEN-4159). provider-api
   * renders the DRAFT PDF from the persisted session `state.canonicalOrder`, so the review-time
   * provider/authorizing/encounter selections (which the SPA otherwise only merges at submit)
   * must be folded into the canonical and PATCHed to the session first — otherwise Sections
   * C/D/G render stale/empty (concern C-1). This mirrors the provider-merge that
   * `handleSubmitOrder` performs, minus the submit-only consent/attestation build.
   */
  const previewCanonicalOrder = useMemo((): CanonicalOrderPayload | null => {
    if (!storedCanonical) return null
    const ehrCtx =
      isEpicFlow && epicState
        ? mapEpicContextToEhrContext(epicState, selectedEncounterId.trim() || null)
        : null
    const formInfoForProviders = applySelfProviderContact(
      orderFormInfoDataWithAuthorizing ?? orderFormInfoData,
      selfProfile,
      currentUserAuth.id,
      currentUserAuth.namePartsAreDerived
    )
    const formProviders = formInfoForProviders
      ? canonicalProvidersFromOrderFormInfo(formInfoForProviders)
      : null
    const mergedProviders = mergeCanonicalProviders(storedCanonical.providers, formProviders)
    return {
      ...storedCanonical,
      ...(ehrCtx && { ehrContext: ehrCtx }),
      ...(mergedProviders != null && { providers: mergedProviders }),
    }
  }, [
    storedCanonical,
    orderFormInfoDataWithAuthorizing,
    orderFormInfoData,
    selfProfile,
    currentUserAuth.id,
    currentUserAuth.namePartsAreDerived,
    isEpicFlow,
    epicState,
    selectedEncounterId,
  ])

  // Serialized snapshot of the exact selection the coverage check depends on (encounter +
  // authorizing/ordering providers + EHR context). This changes synchronously the moment the user
  // changes the encounter or authorizing clinician — before the debounced session PATCH advances
  // `draftPreviewToken` — so comparing it to the token the last check ran against lets us flag a
  // re-check as pending immediately, not only after the session catches up.
  const currentSelectionToken = useMemo(
    () => (previewCanonicalOrder ? JSON.stringify(previewCanonicalOrder) : null),
    [previewCanonicalOrder]
  )

  // Identity of a single coverage evaluation: the session-synced selection snapshot AND the exact
  // authorizing NPI sent in the request body. The result MUST be tagged with, and compared against,
  // this composite — not `draftPreviewToken` alone — because the body's `authorizingNpi` is derived
  // separately from the session state. Tagging with only the session token would let a result that
  // evaluated a different NPI be treated as "current" for a selection that merely serializes the
  // same (a stale-trust race that, since the SPA now gates submit, could pass an ineligible
  // authorizing clinician). Null when there is no selection to key on.
  const coverageSelectionKey = useMemo(
    () =>
      currentSelectionToken == null
        ? null
        : JSON.stringify({ selection: currentSelectionToken, authorizingNpi: authorizingNpiForCoverage || '' }),
    [currentSelectionToken, authorizingNpiForCoverage]
  )

  /**
   * Persist `previewCanonicalOrder` to the session (debounced) so the server-side DRAFT-PDF
   * endpoint renders the exact form the user is reviewing, then advance `draftPreviewToken` to
   * let `RequisitionFormView` fetch. Skipped while submitting (submit does its own authoritative
   * PATCH). Only touches the persisted session — Redux `storedCanonical` remains the submit SoT.
   */
  const [draftPreviewToken, setDraftPreviewToken] = useState<string | null>(null)
  /**
   * Serialized selection snapshot whose preview session PATCH failed. When this equals the current
   * selection, the session did NOT sync for it — so the coverage check must be skipped and the
   * gate must fail open (no pending, no block) rather than run against stale session state or wait
   * out the full pending-timeout. Cleared once that same selection successfully syncs.
   */
  const [draftPreviewSyncFailedToken, setDraftPreviewSyncFailedToken] = useState<string | null>(null)
  const lastPreviewPatchRef = useRef<string | null>(null)
  useEffect(() => {
    if (!passedSessionId || !previewCanonicalOrder || !selfProfileSettled || isSubmitting) return
    const serialized = JSON.stringify(previewCanonicalOrder)
    // Already persisted this exact canonical — just make sure the viewer can fetch.
    if (lastPreviewPatchRef.current === serialized) {
      setDraftPreviewToken(serialized)
      return
    }
    let cancelled = false
    const ac = new AbortController()
    sessionPatchAbortRef.current?.abort()
    sessionPatchAbortRef.current = ac
    if (sessionPatchTimerRef.current) {
      clearTimeout(sessionPatchTimerRef.current)
    }
    const handle = setTimeout(async () => {
      try {
        const providerClient = ProviderAPIClient.getInstance()
        await providerClient.patchOrderingSession(
          passedSessionId,
          { state: { canonicalOrder: previewCanonicalOrder } },
          ac.signal
        )
        // Only record success once we know this PATCH was NOT superseded. Setting
        // `lastPreviewPatchRef` for a cancelled/aborted PATCH would let a later revert to this same
        // selection short-circuit to "already synced" while the server may hold a different (stale)
        // context from an out-of-order completion — the coverage check would then trust the wrong
        // encounter/patient. On cancel we treat this as never-persisted so the next render re-PATCHes.
        if (!cancelled && !ac.signal.aborted) {
          lastPreviewPatchRef.current = serialized
          setDraftPreviewSyncFailedToken((prev) => (prev === serialized ? null : prev))
          setDraftPreviewToken(serialized)
        }
      } catch (error) {
        if (error instanceof OrderingSessionGoneError) {
          handleSessionGone()
          return
        }
        // An aborted PATCH was intentionally superseded by a newer selection — not a sync failure.
        // Leave `lastPreviewPatchRef` untouched (so this selection re-PATCHes if reselected) and do
        // not record a sync failure; the newer effect run owns the current selection's sync.
        if (cancelled || ac.signal.aborted) return
        Sentry.captureException(error, { tags: { operation: 'order.review.preview.session.patch' } })
        // Fail-open WITHOUT advancing draftPreviewToken: the session did NOT sync, so the coverage
        // check must not run against stale session state (a stale/fail-closed result could otherwise
        // be treated as authoritative and hard-block Submit). Instead, record that this selection's
        // sync failed; `coveragePending`/the banners treat a failed-sync selection as fail-open (no
        // check, no block). We deliberately do NOT set `lastPreviewPatchRef`, so the next render
        // retries the PATCH — a transient failure still gets another chance to persist + sync.
        setDraftPreviewSyncFailedToken(serialized)
      }
    }, 400)
    sessionPatchTimerRef.current = handle
    return () => {
      cancelled = true
      ac.abort()
      clearTimeout(handle)
      if (sessionPatchTimerRef.current === handle) {
        sessionPatchTimerRef.current = null
      }
    }
  }, [passedSessionId, previewCanonicalOrder, selfProfileSettled, isSubmitting, handleSessionGone])

  /**
   * Advisory coverage-eligibility check (e.g. CCS), decoupled from submit. The server reads the
   * authorizing provider + encounter from the persisted session state, which is only written by
   * the debounced preview PATCH above. So this must run AFTER the session reflects the current
   * selection — not merely when local selection state changes — or it would evaluate stale
   * context. We gate on `draftPreviewToken`, which advances to the serialized `previewCanonicalOrder`
   * exactly when the PATCH for that canonical has landed (or already matched). Re-running keyed on
   * that token means every encounter / authorizing-provider change re-issues the check once the
   * session catches up. Epic flow only; failures fail-open (no result → no block).
   */
  useEffect(() => {
    if (!isEpicFlow || !epicState || !passedSessionId) return
    // Defer until any encounter / authorizing-provider retrieval settles.
    if (patientEncountersLoading || authorizingProviderLoading) return
    // Only run once the session state holds the CURRENT selection. It is not enough for
    // `draftPreviewToken` to merely be non-null: a stale token from a prior selection would let the
    // check evaluate this render's `authorizingNpiForCoverage` while getting tagged with the old
    // selection — a trust race. Requiring `draftPreviewToken === currentSelectionToken` guarantees
    // the persisted session, the NPI in the body, and the tag all describe the same selection.
    if (!draftPreviewToken || draftPreviewToken !== currentSelectionToken) return

    let cancelled = false
    const ac = new AbortController()
    // Tag the result with the full evaluation identity (session snapshot + NPI sent), so
    // `coverageResultIsCurrent` only trusts it for the exact same inputs.
    const checkedKey = coverageSelectionKey
    const run = async () => {
      const ehrAccessToken = EpicAuthClient.getInstance().getAccessToken(epicState) || undefined
      const body: CoverageCheckRequest = {}
      if (ehrAccessToken) body.ehrAccessToken = ehrAccessToken
      if (authorizingNpiForCoverage) body.authorizingNpi = authorizingNpiForCoverage
      const result = await ProviderAPIClient.getInstance().coverageCheck(
        passedSessionId,
        Object.keys(body).length > 0 ? body : undefined,
        ac.signal
      )
      if (!cancelled && !ac.signal.aborted) {
        setCoverageResult(result)
        setCoverageCheckedKey(checkedKey)
      }
    }
    void run()
    return () => {
      cancelled = true
      ac.abort()
    }
  }, [
    isEpicFlow,
    epicState,
    passedSessionId,
    draftPreviewToken,
    currentSelectionToken,
    coverageSelectionKey,
    patientEncountersLoading,
    authorizingProviderLoading,
    authorizingNpiForCoverage,
  ])

  /**
   * Final submit workflow: validates the selected attestor, attaches consent/EHR
   * context, persists the final canonical payload, then sends it to provider-api.
   */
  const handleSubmitOrder = async () => {
    const clinicalTextErrorNow = clinicalTextLimitError(getFulfillmentStrategy(config), storedCanonical)
    if (clinicalTextErrorNow) {
      setSubmitError(clinicalTextErrorNow)
      return
    }
    const orderDataToSubmit = orderFormInfoDataWithAuthorizing ?? orderFormInfoData

    // ── Guards: session path requires storedCanonical; legacy path requires form data ───
    if (passedSessionId) {
      if (!storedCanonical) {
        setSubmitError('Order data is missing. Please go back to the order form and try again.')
        return
      }
    } else if (!orderDataToSubmit || !formData) {
      setSubmitError('Unable to submit order without patient data.')
      return
    }
    // NPI validation for the authorizing clinician. Two independent cases must be caught:
    //  1. The current user lacks an NPI → they must submit on behalf of an NPI-bearing clinician.
    //  2. An authorizing provider IS explicitly selected (e.g. an auto-resolved encounter
    //     attending, which is now applied even without an NPI for display) → that selection must
    //     carry an NPI regardless of whether the current user has one, otherwise the order would
    //     ship with a no-NPI authorizing provider.
    if (orderFormInfoData) {
      const authNpi = resolvedAuthorizingSelectionNpi(selectedAuthorizingProvider)
      if (!currentUserHasNpi && !authNpi) {
        setSubmitError(
          isEpicFlow
            ? 'Your account does not have an NPI. Search and select a clinician with an NPI to submit on their behalf.'
            : 'Your account does not have an NPI. Select an authorizing clinician from your organization’s list to submit on their behalf.'
        )
        return
      }
      if (selectedAuthorizingProvider != null && !authNpi) {
        setSubmitError(
          'The selected authorizing clinician does not have an NPI on file. Select a clinician with an NPI to submit on their behalf.'
        )
        return
      }
    }
    setSubmitError(null)
    setIsSubmitting(true)

    // Guarantee the current user's phone / medical title are loaded before building the
    // canonical payload so a fast submit cannot ship a blank ordering-provider phone
    // (which the provider-api replaces with the 000-000-0000 fallback).
    const resolvedSelfProfile = await fetchSelfProfile()

    /** Backfill the submitter's own phone / roleTitle onto the ordering provider (shared helper). */
    const withSelfProviderContact = (info: OrderFormInfoData | null): OrderFormInfoData | null =>
      applySelfProviderContact(
        info,
        resolvedSelfProfile,
        currentUserAuth.id,
        currentUserAuth.namePartsAreDerived
      )

    await Sentry.startSpan({ op: 'ui.click', name: 'Submit Order Ready To Send' }, async (span) => {
      try {
        let finalOrderId: string | null = null

        if (passedSessionId) {
          // ── Session path (canonical-first) ─────────────────────────────────
          const providerClient = ProviderAPIClient.getInstance()

          const ehrCtx =
            isEpicFlow && epicState
              ? mapEpicContextToEhrContext(epicState, selectedEncounterId.trim() || null)
              : null

          const formInfoForProviders = withSelfProviderContact(
            orderFormInfoDataWithAuthorizing ?? orderFormInfoData
          )
          const formProviders = formInfoForProviders
            ? canonicalProvidersFromOrderFormInfo(formInfoForProviders)
            : null
          // Send both the logged-in submitter (`orderingProvider`) and the `authorizingProvider`
          // exactly as captured. Provider-api now performs the "on behalf of" handling itself:
          //  - orderInfo provider is sourced from `authorizingProvider` (falling back to
          //    `orderingProvider`), matching TRF Section C, and
          //  - `authorizedDelegate` is stamped from the submitting provider only when the ordering
          //    and authorizing providers differ (NPI/email/name).
          // The UI must therefore preserve the ordering-vs-authorizing split rather than collapsing
          // it client-side, or the API can no longer detect the delegate. This is now uniform for
          // web and EHR/Epic submits.
          const providersForSubmit = mergeCanonicalProviders(storedCanonical!.providers, formProviders)

          const testCode = storedCanonical!.order?.selectedOption?.trim() || ''
          const effectiveConsentFlow = effectiveConsentFlowForSessionSubmit
          const isPractitionerMediatedSubmit = effectiveConsentFlow === 'PRACTITIONER_MEDIATED'
          const builtPc = buildCanonicalPrimaryConsent({
            name: authorizingProviderName,
            npi: authorizingProviderNpi,
            consentedText: providerConsentText || null,
          })
          const prevPc = storedCanonical!.primaryConsent
          const guardian = storedCanonical!.guardianContact
          // Match provider-api: missing orderType is treated as PROBAND_ONLY (map_intent / submit).
          const effectiveOrderType =
            (storedCanonical!.orderType && storedCanonical!.orderType.trim()) || 'PROBAND_ONLY'
          const prevFm = prevPc?.familyMember
          let familyMemberForSubmit: PrimaryConsentFamilyMember | undefined = prevFm ?? undefined
          // Proband + guardian: always attach familyMember + signatureDate before patch/submit.
          // provider-api only *validates* signatureDate for PRACTITIONER_MEDIATED + guardian capture;
          // we do not gate on PM here so a slow/missing ordering-domain fetch cannot cause a 422.
          if (effectiveOrderType === 'PROBAND_ONLY' && guardian && guardian.contactNotAvailable !== true) {
            const fn = (prevFm?.firstName?.trim() || guardian.firstName?.trim() || '').trim()
            const ln = (prevFm?.lastName?.trim() || guardian.lastName?.trim() || '').trim()
            if (fn && ln) {
              const signatureDate =
                prevFm?.signatureDate?.trim() ||
                builtPc.practitioner?.signatureDate?.trim() ||
                getLocalCalendarDateString()
              familyMemberForSubmit = {
                ...prevFm,
                firstName: fn,
                lastName: ln,
                // Include DOB so it round-trips through primary_consent and is
                // available to rehydrate the Edit Guardian Contact modal after
                // submit. canonicalOrder.guardianContact does not always survive
                // the server round-trip on proband-only orders.
                dateOfBirth: prevFm?.dateOfBirth?.trim() || guardian.dateOfBirth?.trim() || undefined,
                email: prevFm?.email?.trim() || guardian.email?.trim() || undefined,
                phone: prevFm?.phone?.trim() || guardian.phone?.trim() || undefined,
                relationship:
                  prevFm?.relationship ??
                  guardianRelationshipToFamilyMember(guardian.relationshipToPatient),
                signatureDate,
                signerDisplayName:
                  prevFm?.signerDisplayName?.trim() || `${fn} ${ln}`.trim() || undefined,
              }
            }
          }
          const canonicalOrder: CanonicalOrderPayload = {
            ...storedCanonical!,
            ...(ehrCtx && { ehrContext: ehrCtx }),
            primaryConsent: {
              ...prevPc,
              practitioner: builtPc.practitioner,
              familyMember: familyMemberForSubmit ?? prevPc?.familyMember,
              primaryConsentKey: prevPc?.primaryConsentKey?.trim() || null,
              consentType:
                prevPc?.consentType?.trim() ||
                (isPractitionerMediatedSubmit ? 'ATTESTATION' : null),
            },
            ...(providersForSubmit != null && { providers: providersForSubmit }),
          }

          await providerClient.patchOrderingSession(passedSessionId, { state: { canonicalOrder } })
          span.setAttribute('order.action', 'session-patch-final')
          span.setAttribute('session.id', passedSessionId)

          const resolvedOrgId = await resolveImdbOrgIdForOrderingApi({
            orgIdFromAuth: orgId,
            additionalUuidCandidate: (orderDataToSubmit as { organizationId?: string } | null)
              ?.organizationId,
            isEpicFlow,
            epicOAuthState: epicState || undefined,
          })
          if (!resolvedOrgId) {
            throw new Error(
              'Organization context is missing or invalid for submit. Re-launch from Epic or refresh and try again.'
            )
          }
          // Redox write-back (provider-api): only opt in when Epic + a concrete Encounter id is chosen
          // so environments without REDOX_CLIENT_ID still submit; with Redox, server needs encounter + token.
          const submitViaRedox = isEpicFlow && Boolean(selectedEncounterId.trim())

          const submitResult = await providerClient.submitOrderingSession(
            passedSessionId,
            {
              sessionId: passedSessionId,
              organizationId: resolvedOrgId,
              orderType: canonicalOrder.orderType,
              tests: canonicalOrder.order?.selectedOption
                ? [
                    {
                      code: canonicalOrder.order.selectedOption,
                      // Explicit flow on intent: use resolved effective flow (catalog + org default),
                      // not raw catalog DEFAULT, so provider-api logs INTENT_TEST_LINE for PM.
                      consentFlowId: effectiveConsentFlow,
                    },
                  ]
                : [],
              configVersion: configVersion || config.version,
              ehrAccessToken:
                isEpicFlow && epicState ? EpicAuthClient.getInstance().getAccessToken(epicState) : null,
              sendFamilyWelcomeEmail: config.featureFlags.sendFamilyWelcomeEmail === true,
            },
            { submitViaRedox }
          )
          finalOrderId = (submitResult['orderId'] as string) ?? null
          span.setAttribute('session.submit.orderId', finalOrderId ?? '')
          Sentry.logger.info('ordering.submit.session_succeeded', {
            sessionId: passedSessionId,
            orderId: finalOrderId,
            orderType: effectiveOrderType,
            testCode: testCode || null,
            effectiveConsentFlow: effectiveConsentFlow || null,
            intentConsentFlowId: effectiveConsentFlow,
            hasGuardianContact: Boolean(storedCanonical?.guardianContact),
            guardianContactNotAvailable:
              storedCanonical?.guardianContact?.contactNotAvailable ?? null,
            primaryConsentFamilyMemberOnPayload: Boolean(canonicalOrder.primaryConsent?.familyMember),
          })
        } else {
          // ── Legacy fallback path (no session) — IMDB direct submit ─────────
          // This path is exercised for pre-session orders (e.g. auth-callback flow).
          // Log a warning so we know if this is still exercised in production.
          Sentry.logger.warn('Legacy submit path taken (no sessionId)', {
            orderType: storedCanonical?.orderType ?? 'unknown',
            hasCanonical: Boolean(storedCanonical),
          })
          const caller = getCallerName(orderDataToSubmit)
          const legacyOrderData = withSelfProviderContact(orderDataToSubmit ?? null) ?? orderDataToSubmit
          const legacyTestCode =
            storedCanonical?.order?.selectedOption?.trim() ||
            String(formData!.order?.selectedOption || '').trim()
          const legacyEffectiveConsentFlow = resolveEffectiveConsentFlowForSubmit(
            config,
            legacyTestCode,
            domainTestCatalog
          )
          const persistOpts = {
            canonicalOrder: storedCanonical,
            consentFlowId: legacyEffectiveConsentFlow || null,
          }
          const consentFormData = buildConsentFormData(formData!)
          const cleanedFormData = cleanupPartialData({
            ...formData!,
            consentFormData,
          })

          let orderIdToSubmit: string | null = passedOrderId

          if (!orderIdToSubmit) {
            const existingOrder = await getOrderByPatient(orderDataToSubmit!.patient.id, caller)
            if (existingOrder?.id) {
              orderIdToSubmit = existingOrder.id
            }
          }

          if (orderIdToSubmit) {
            const existingOrder = await getOrderByPatient(orderDataToSubmit!.patient.id, caller)
            if (existingOrder && existingOrder.status !== OrderStatus.DRAFT) {
              setSubmitError(
                `This order has already been submitted and cannot be resubmitted. Current status: ${existingOrder.status}`
              )
              setIsSubmitting(false)
              return
            }
          }

          const submitExtras = isEpicFlow
            ? {
                access_token: epicState ? EpicAuthClient.getInstance().getAccessToken(epicState) : null,
                ehr_encounter_id: selectedEncounterId.trim() || null,
                submit_via_redox: true,
              }
            : undefined

          if (orderIdToSubmit) {
            await submitOrder(orderIdToSubmit, legacyOrderData!, cleanedFormData, caller, submitExtras, persistOpts)
            span.setAttribute('order.action', 'submit-update')
            span.setAttribute('order.id', orderIdToSubmit)
            finalOrderId = orderIdToSubmit
          } else {
            const createdOrder = await createOrder(
              legacyOrderData!,
              cleanedFormData,
              OrderStatus.DRAFT,
              caller,
              persistOpts
            )
            if (createdOrder?.id) {
              await submitOrder(
                String(createdOrder.id),
                legacyOrderData!,
                cleanedFormData,
                caller,
                submitExtras,
                persistOpts
              )
              span.setAttribute('order.action', 'submit-create')
              span.setAttribute('order.id', String(createdOrder.id))
              finalOrderId = String(createdOrder.id)
            } else {
              throw new Error('Failed to create order')
            }
          }
        }

        // Persist encounter for in-progress PUT Redox fields when canonical omits ehrContext after IMDB round-trip.
        if (isEpicFlow && selectedEncounterId.trim() && finalOrderId) {
          setEpicEhrEncounterForOrder(String(finalOrderId), selectedEncounterId.trim())
        }

        setSubmittedOrderId(finalOrderId ?? undefined)
        setSubmittedOrderData(withSelfProviderContact(orderDataToSubmit ?? null) ?? orderDataToSubmit ?? null)

        if (!finalOrderId) {
          // Submit resolved but we cannot confirm or locate the order. Do not claim
          // clean success on any lane: no modal, no /sign redirect. Disable the CTA
          // permanently so the already-submitted order cannot be re-submitted.
          setSubmitDegradedNotice(
            'Order submitted but confirmation is unavailable. Check your orders before resubmitting.'
          )
          return
        }

        if (signingCopy.lane === 'embedded') {
          // Stash the signing intent before leaving: this is a hard navigation, so the
          // optimistic "checking your signature status" cue on the order page can only
          // survive it through sessionStorage. Without it a clinician returning from the
          // ceremony sees the pre-sign status with Sign still clickable until the poller
          // catches up.
          markTrfSigningInProgress(finalOrderId)
          // Full-page navigate to the embedded signing experience. The iframe requires
          // the /sign* path to satisfy CloudFront CSP. Never open the modal on this path.
          window.location.assign(trfSigningPath(finalOrderId))
        } else {
          const clinicianName = selectedAuthorizingProvider != null
            ? authorizingProviderName || null
            : currentUserPhysicianName || null
          setSignatureRequestedCopy(orderSuccessSigningCopy({ lane: signingCopy.lane, clinicianName }))
          setShowSuccessModal(true)
        }
      } catch (error) {
        Sentry.captureException(error, { tags: { operation: 'order.submit' } })
        const errorMessage =
          error instanceof Error
            ? error.message
            : copyWithoutSigning(
                "Couldn't complete and sign this order. Try again.",
                isTrfSignatureDocusignEnabled(config)
              )
        // The client config is now pinned server-side for the life of the order,
        // so config drift no longer blocks submission (no CONFIG_CHANGED path).
        setSubmitError(errorMessage)
      } finally {
        setIsSubmitting(false)
      }
    })
  }

  /**
   * Routes the user to the in-progress order workspace where they can track
   * downstream lab/EHR status for the submitted order. Shared by the legacy
   * ``navigateInProgress`` action and by ``returnToDashboard`` when launched
   * from an EHR (Epic) context, where there is no standalone app dashboard to
   * return to.
   */
  const navigateToOrderInProgress = () => {
    const patientId = (submittedOrderData ?? orderFormInfoData)?.patient?.id
    const path = `/order/in-progress/${submittedOrderId || ''}/${patientId || ''}`
    if (passedSessionId) {
      navigate(path, {
        state: {
          epicState,
          orderId: submittedOrderId,
          orderData: submittedOrderData ?? orderFormInfoData ?? undefined,
          sessionId: passedSessionId,
          // Arm the in-progress page's "preparing your document for signature"
          // state across the window before the envelope reaches `sent` (GEN-5027).
          justSubmitted: true,
        },
      })
    } else {
      navigate(path, {
        state: {
          epicState,
          orderId: submittedOrderId,
          orderData: submittedOrderData ?? orderFormInfoData ?? undefined,
          formData,
          // Arm the in-progress page's "preparing your document for signature"
          // state across the window before the envelope reaches `sent` (GEN-5027).
          justSubmitted: true,
        },
      })
    }
  }

  /**
   * Default close behavior for the success modal (legacy ``navigateInProgress``
   * action). Sends the user to the in-progress order workspace.
   */
  const handleSuccessClose = () => {
    setShowSuccessModal(false)
    navigateToOrderInProgress()
  }

  /**
   * Handler for the configurable ``returnToDashboard`` order-success action.
   *
   * The button label is configurable per tenant (e.g. "Return to Dashboard" vs
   * "Return to Workspace"), but the destination is determined by the launch
   * context, not the label:
   *   - Web App (standalone) → orders workspace at ``/orders``
   *   - Epic Launch          → in-progress order page (no app dashboard exists
   *                            inside the EHR launch surface)
   */
  const handleReturnToDashboard = () => {
    setShowSuccessModal(false)
    if (isEpicFlow) {
      navigateToOrderInProgress()
      return
    }
    navigate('/orders', { replace: true })
  }

  /**
   * Handler for the configurable ``printOrderForm`` order-success action.
   *
   * Mirrors the in-progress page's "View Form" experience: dismiss the success
   * modal and open the requisition PDF in ``OrderFormViewModal``. The viewer's
   * toolbar exposes "Print" and "Open in New Window", both of which spawn a tab
   * holding the requisition's pages and print from there.
   */
  const handlePrintOrderForm = () => {
    setShowSuccessModal(false)
    setIsPrintFormModalOpen(true)
  }

  /**
   * Closes the requisition PDF modal opened by ``printOrderForm`` and re-shows
   * the order-success modal.
   *
   * Without this, dismissing the PDF viewer would land the user back on the
   * Submit page with the Submit button still interactive (the order has been
   * successfully sent server-side, but the page state still permits a click),
   * making it possible to accidentally re-submit the same order. Re-opening
   * the success modal forces an explicit "Return to Dashboard" / navigate-away
   * choice, which is the only legal next step once an order has been sent.
   */
  const handlePrintFormModalClose = () => {
    setIsPrintFormModalOpen(false)
    setShowSuccessModal(true)
  }

  /**
   * Handler for the configurable ``generateShippingLabel`` order-success action.
   *
   * Dismisses the success modal, opens the ShippingLabelModal in a loading
   * state, then requests a FedEx kit label via ``POST /kits/shipping-label``
   * (addressFrom = ``orgAddress``, addressTo = ``wetLab.shipToAddress``). When
   * either address is missing/incomplete we show a config error and never call
   * the endpoint. Honest by construction: an ``ok:false`` / non-2xx / network
   * failure renders an error state rather than a blank preview.
   */
  const handleGenerateShippingLabel = async () => {
    setShowSuccessModal(false)
    setIsShippingLabelModalOpen(true)
    setShippingLabelState({ status: 'loading' })

    const addresses = resolveShippingAddresses(config)
    if (!addresses.ok) {
      setShippingLabelState({ status: 'error', error: addresses.error })
      return
    }

    const result = await requestShippingLabel(ProviderAPIClient.getInstance(), {
      orderId: submittedOrderId,
      addressFrom: addresses.addressFrom,
      addressTo: addresses.addressTo,
    })

    if (!result.ok) {
      setShippingLabelState({ status: 'error', error: result.error })
      return
    }

    setShippingLabelState({
      status: 'ready',
      labelUrl: result.labelUrl,
      trackingNumber: result.trackingNumber,
      labelFileType: result.labelFileType,
    })
  }

  /**
   * Closes the ShippingLabelModal and re-shows the order-success modal (same
   * rationale as :func:`handlePrintFormModalClose`: the order is already sent,
   * so returning to the Submit page could invite an accidental re-submit).
   */
  const handleShippingLabelModalClose = () => {
    setIsShippingLabelModalOpen(false)
    setShippingLabelState({ status: 'idle' })
    setShowSuccessModal(true)
  }

  /** Keeps the draft/session intact and returns to OrderStart for edits. */
  const handleCancel = async () => {
    const backState =
      authFlow === 'standalone'
        ? {
            authFlow: 'standalone' as const,
            ...(orderFormInfoData && {
              patientData: orderFormInfoData,
              orderId: passedOrderId ?? undefined,
            }),
            ...(passedSessionId && { sessionId: passedSessionId }),
          }
        : {
            epicState,
            ...(orderFormInfoData && { patientData: orderFormInfoData, orderId: passedOrderId ?? undefined }),
            ...(passedSessionId && { sessionId: passedSessionId }),
          }

    const startPath = passedSessionId ? `/order/start?sessionId=${passedSessionId}` : '/order/start'

    if (!orderFormInfoData || passedSessionId) {
      // Session path: form data is already in Redux from when it was passed to this screen;
      // no IMDB draft lookup needed.
      navigate(startPath, { state: backState })
      return
    }
    // Legacy path: reload form data from IMDB draft before navigating back.
    try {
      const caller = getCallerName(orderFormInfoData)
      const existingOrder = await getOrderByPatient(orderFormInfoData.patient.id, caller)
      if (existingOrder?.json_payload?.requisitionFormData) {
        dispatch(setCanonicalOrder(legacyTrfPayloadToCanonical({
          orderFormInfo: existingOrder.json_payload.orderFormInfo,
          requisitionFormData: existingOrder.json_payload.requisitionFormData,
        })))
      }
    } catch (error) {
      Sentry.captureException(error, { tags: { operation: 'order.draft.load' } })
    } finally {
      navigate(startPath, { state: backState })
    }
  }

  /** Logs the standalone user out from the review page and returns them to the login screen. */
  const handleLogout = () => {
    Sentry.startSpan(
      {
        op: 'ui.click',
        name: 'Logout from Order Ready Page',
      },
      (span) => {
        span.setAttribute('auth.flow', 'standalone')
        auth0Logout({
          logoutParams: {
            returnTo: window.location.origin + '/login',
          },
        })
      }
    )
  }

  const missingCurrentUserNpi = !!orderFormInfoData && !currentUserHasNpi
  const hasEpicEncounter = isEpicFlow && Boolean(selectedEncounterId.trim())
  const selectedEncounterHasAttending = Boolean(selectedPatientEncounter?.attendingPractitionerId)

  const cannotUncheckOnBehalfOf =
    missingCurrentUserNpi && !(hasEpicEncounter && !selectedEncounterHasAttending)

  const isOnBehalfOfChecked = submitOnBehalfOfAnother || cannotUncheckOnBehalfOf

  const missingNpiNeedsAuthorizing =
    missingCurrentUserNpi && !resolvedAuthorizingSelectionNpi(selectedAuthorizingProvider)

  /**
   * Epic + on-behalf: require an NPI-bearing authorizing selection (manual search or ATND read).
   * Applies whether or not an encounter is present — in Epic without an encounter the user can
   * still opt into on-behalf and type a name, which must not be submittable without an NPI-bearing
   * selection.
   *
   * Blocks when either:
   *   - the user has typed/kept an on-behalf name that differs from the logged-in user but no
   *     NPI-bearing selection backs it, OR
   *   - an authorizing provider IS selected (e.g. an auto-resolved encounter attending) but that
   *     selection has no NPI. The auto-resolve applies a no-NPI attending for display, so the
   *     name-match shortcut alone must NOT be trusted — a selected no-NPI clinician can share the
   *     logged-in user's name yet still be invalid to submit.
   */
  const epicManualAuthorizingIncomplete =
    isEpicFlow &&
    submitOnBehalfOfAnother &&
    (submittingOnBehalfOf.trim() !== providerName || selectedAuthorizingProvider != null) &&
    !resolvedAuthorizingSelectionNpi(selectedAuthorizingProvider)

  const standaloneOnBehalfIncomplete =
    !isEpicFlow &&
    submitOnBehalfOfAnother &&
    !cannotUncheckOnBehalfOf &&
    !selectedAuthorizingProvider

  // Advisory insurance-coverage eligibility (e.g. CCS). This is decoupled from submit:
  // POST /ordering/sessions/{id}/coverage-check returns per-dimension eligibility. The SPA
  // surfaces a warning and blocks submit when a coverage type applies to this case but the
  // provider is not eligible. The `provider` bit reflects the *authorizing* clinician's
  // credentials — the one who will authorize the order (the selected authorizing clinician when
  // on-behalf, else the logged-in user) — which is why the check is re-issued whenever the
  // authorizing provider (or encounter) changes so the server evaluates the right provider. The
  // check runs in the Epic flow only (the standalone flow has no coverage-check wiring yet).
  //
  // Blocking is derived per the API contract, generically across all returned checks:
  //   applies = patient && encounter   (coverage relevant to this case)
  //   blocked = applies && !provider   (authorizing provider not eligible for an applicable case)
  // Today patient/encounter come back false (placeholder Epic reads), so `applies` is false and
  // no warning shows until those attributes land — this is expected.
  // The settled `coverageResult` is only trustworthy while it was computed against the current
  // selection AND the current authorizing NPI. `coverageCheckedKey` holds the composite
  // `coverageSelectionKey` the check ran with; comparing it to the live key ensures a result is
  // never trusted for a selection/NPI it did not actually evaluate (closes the stale-trust race).
  // When stale, we treat coverage as having no result.
  const coverageResultIsCurrent =
    coverageResult != null && coverageCheckedKey != null && coverageCheckedKey === coverageSelectionKey
  const effectiveCoverageChecks = useMemo(
    () => (coverageResultIsCurrent ? coverageResult?.checks ?? [] : []),
    [coverageResultIsCurrent, coverageResult]
  )

  const coverageBlockingChecks = useMemo(
    () => effectiveCoverageChecks.filter((check) => check.patient && check.encounter && !check.provider),
    [effectiveCoverageChecks]
  )
  const coverageWarning = coverageBlockingChecks.length > 0
  // Human-readable list of the coverage programs that triggered the warning (e.g.
  // "CCS (California Children's Services)"). Used for the first mention.
  const coverageWarningLabel = useMemo(() => {
    const labelFor = (type: string) => (type === 'CCS' ? 'CCS (California Children\u2019s Services)' : type)
    const labels = Array.from(new Set(coverageBlockingChecks.map((c) => labelFor(c.coverageType))))
    return labels.join(', ')
  }, [coverageBlockingChecks])
  // Short label (just the coverage type, e.g. "CCS") for subsequent mentions.
  const coverageWarningLabelShort = useMemo(() => {
    const types = Array.from(new Set(coverageBlockingChecks.map((c) => c.coverageType)))
    return types.join(', ')
  }, [coverageBlockingChecks])

  // Satisfied checks: an applicable coverage type (patient && encounter) whose authorizing provider
  // IS eligible. Surfaced as a positive confirmation so the user knows the credential requirement is
  // met (e.g. the authorizing clinician is CCS credentialed). Purely informational — never gates.
  const coverageSatisfiedChecks = useMemo(
    () => effectiveCoverageChecks.filter((check) => check.patient && check.encounter && check.provider),
    [effectiveCoverageChecks]
  )
  const coverageSatisfied = coverageSatisfiedChecks.length > 0
  const coverageSatisfiedLabel = useMemo(() => {
    const labelFor = (type: string) => (type === 'CCS' ? 'CCS (California Children\u2019s Services)' : type)
    const labels = Array.from(new Set(coverageSatisfiedChecks.map((c) => labelFor(c.coverageType))))
    return labels.join(', ')
  }, [coverageSatisfiedChecks])

  // Not-applicable checks: an enabled coverage type that does NOT apply to this case because the
  // patient and/or encounter dimension did not satisfy CCS (e.g. the patient isn't CCS-covered or
  // the encounter isn't a qualifying hospital encounter). Purely informational — this never gates
  // submit (the API contract defines `applies = patient && encounter`), it just tells the user why
  // no CCS credential requirement is being enforced for this order.
  const coverageNotApplicableChecks = useMemo(
    () => effectiveCoverageChecks.filter((check) => !(check.patient && check.encounter)),
    [effectiveCoverageChecks]
  )
  const coverageNotApplicable = coverageNotApplicableChecks.length > 0
  const coverageNotApplicableLabel = useMemo(() => {
    const labelFor = (type: string) => (type === 'CCS' ? 'CCS (California Children\u2019s Services)' : type)
    const labels = Array.from(new Set(coverageNotApplicableChecks.map((c) => labelFor(c.coverageType))))
    return labels.join(', ')
  }, [coverageNotApplicableChecks])
  // Which dimension(s) failed, aggregated across not-applicable checks, for a precise explanation.
  const coverageNotApplicableReason = useMemo(() => {
    const patientMissing = coverageNotApplicableChecks.some((c) => !c.patient)
    const encounterMissing = coverageNotApplicableChecks.some((c) => !c.encounter)
    if (patientMissing && encounterMissing) return 'the patient and encounter do not'
    if (patientMissing) return 'the patient does not'
    if (encounterMissing) return 'the encounter does not'
    return 'this order does not'
  }, [coverageNotApplicableChecks])

  // The current selection's preview session PATCH failed, so the session did not sync for it. We
  // fail open immediately: skip the pending gate (don't wait out the full timeout) and, since no
  // fresh check can be trusted, don't block. A stale prior result is already gated out by
  // `coverageResultIsCurrent` (its token won't match the current selection).
  const currentSyncFailed =
    draftPreviewSyncFailedToken != null && draftPreviewSyncFailedToken === currentSelectionToken

  // Coverage is "pending" (Epic flow only) whenever the last completed check does not correspond to
  // the current evaluation identity (`coverageSelectionKey` = session-synced selection + authorizing
  // NPI). This fires the instant the user changes the encounter / authorizing clinician (the key
  // changes immediately) and stays pending until a fresh check tagged with the current key settles —
  // so a stale result can neither allow nor block submit for the wrong inputs. A failed session sync
  // for the current selection short-circuits to not-pending (fail-open). Standalone is never pending.
  const coveragePendingRaw =
    isEpicFlow &&
    Boolean(passedSessionId) &&
    !currentSyncFailed &&
    coverageCheckedKey !== coverageSelectionKey

  // Fail-open safety net: if the raw pending state persists too long (session sync/coverage check
  // never settles for the current selection), force it to clear so an advisory check can never
  // permanently disable Submit. The timer is (re)started whenever the pending state or the current
  // selection changes, and cleared as soon as pending resolves normally.
  useEffect(() => {
    if (!coveragePendingRaw) {
      setCoveragePendingTimedOut(false)
      return
    }
    setCoveragePendingTimedOut(false)
    const handle = setTimeout(() => {
      Sentry.logger.warn('ordering.coverage_check.pending_timeout_failopen', { sessionId: passedSessionId })
      setCoveragePendingTimedOut(true)
    }, 8000)
    return () => clearTimeout(handle)
  }, [coveragePendingRaw, coverageSelectionKey, passedSessionId])

  const coveragePending = coveragePendingRaw && !coveragePendingTimedOut

  // Fail-open net for the optional encounter/attending loading gates: if either retrieval hangs, the
  // loading flags would otherwise keep Submit disabled forever (encounter selection is optional). A
  // timeout releases the loading contribution to `submitDisabled` while leaving the flags — and thus
  // the in-flight spinners/abort semantics — intact. The timer (re)starts whenever the combined
  // loading state changes and clears as soon as both retrievals settle.
  const loadingGateActive = patientEncountersLoading || authorizingProviderLoading
  useEffect(() => {
    if (!loadingGateActive) {
      setLoadingGateTimedOut(false)
      return
    }
    setLoadingGateTimedOut(false)
    const handle = setTimeout(() => {
      Sentry.logger.warn('ordering.loading_gate.timeout_failopen', {
        sessionId: passedSessionId,
        patientEncountersLoading,
        authorizingProviderLoading,
      })
      setLoadingGateTimedOut(true)
    }, 8000)
    return () => clearTimeout(handle)
  }, [loadingGateActive, patientEncountersLoading, authorizingProviderLoading, passedSessionId])

  // Loading contribution to the submit gate, after the fail-open net.
  const loadingGate = loadingGateActive && !loadingGateTimedOut

  // Disable submit until required authorizing-clinician choices are complete, while an
  // encounter / authorizing-provider retrieval is in flight (the coverage check depends on
  // both, and submitting mid-retrieval would use stale context) — subject to a fail-open net so a
  // hung optional retrieval can't block forever (`loadingGate`) — while a coverage re-check for
  // the current selection is still outstanding (coveragePending), or when an applicable coverage
  // check found the authorizing provider ineligible (hard block). A failed/absent coverage-check
  // response (null) does not warn/block — there is no result to gate on — but it still resolves
  // the pending state (the check settled), so failures remain fail-open.
  const clinicalTextError = clinicalTextLimitError(getFulfillmentStrategy(config), storedCanonical)
  const clinicalCombinedOverLimit = clinicalTextError !== null

  const submitDisabled =
    isSubmitting ||
    !attestedTerms ||
    missingNpiNeedsAuthorizing ||
    epicManualAuthorizingIncomplete ||
    standaloneOnBehalfIncomplete ||
    loadingGate ||
    coveragePending ||
    coverageWarning ||
    submitDegradedNotice != null ||
    clinicalCombinedOverLimit

  // A stable identity key for whoever is the current signer of record.
  // Uses the practitioner_id first, then falls back to NPI, then name.
  // When no clinician has been selected the signed-in user ("self") is the signer.
  // Opening the picker alone does not change the signer, so the key is stable until
  // a real selection (or revert-to-self) occurs.
  const signerKey = selectedAuthorizingProvider
    ? (selectedAuthorizingProvider.patientPhysician?.id?.trim() ||
       resolvedAuthorizingSelectionNpi(selectedAuthorizingProvider) ||
       selectedAuthorizingProvider.authorizingProfessionalInfo?.name?.trim() ||
       'unknown-provider')
    : 'self'

  // Clear the attestation whenever the signer of record actually changes.
  // The previous signer's agreement does not carry forward to a new signer.
  const prevSignerKeyRef = useRef(signerKey)
  useEffect(() => {
    const prev = prevSignerKeyRef.current
    prevSignerKeyRef.current = signerKey
    if (prev !== signerKey) {
      setAttestedTerms(false)
    }
  }, [signerKey])

  // The attestor is the clinician whose NPI/signature will be used for consent and submission.
  // Its identity is driven SOLELY by an actual `selectedAuthorizingProvider` so the name, title,
  // and NPI always come from the same source. A transient picker display name (typed text or an
  // encounter attending display before its async read resolves) must NOT drive the attestor — it
  // previously caused the block to show another clinician's name next to the current user's NPI.
  const submittingOnBehalfOfActive: boolean = selectedAuthorizingProvider != null

  const orderSteps = useConfiguredOrderSteps({ currentStepIndex: 0 })

  const signingCopy = readyToSendSigningCopy({
    docusignEnabled: isTrfSignatureDocusignEnabled(config),
    submittingOnBehalfOf: isOnBehalfOfChecked,
    ehrTitle: providerConsent?.ehrSignReminderTitle,
    ehrBody: providerConsent?.ehrSignReminderBody,
  })

  const attestorName = submittingOnBehalfOfActive
    ? authorizingProviderName || 'Unknown provider'
    : currentUserPhysicianName || 'Unknown'
  const attestorNpi = authorizingNpiForCoverage
  // Title/credential of the attestor: the selected authorizing provider's roleTitle when
  // submitting on behalf, otherwise the logged-in user's own profile roleTitle. Config/data-
  // derived free-text that may be absent — render defensively (append only when present).
  const attestorRoleTitle = (
    submittingOnBehalfOfActive
      ? selectedAuthorizingProvider?.patientPhysician.roleTitle
      : selfProfile?.roleTitle
  )?.trim()

  // A reload after an Epic discard drops navigation state, so the launch mode (not
  // `isEpicFlow`) is what still identifies the Epic session here.
  const epicDiscardSurvivedReload =
    (isEpicFlow || launchMode === 'epic_smart') &&
    !passedSessionId &&
    isDiscardMarkerSet(patientLookupId ?? readEpicLaunchPatientId(epicState))
  if (epicDiscarded || epicDiscardSurvivedReload) {
    return <EpicOrderDiscarded />
  }

  return (
    <div className={authFlow === 'standalone' ? 'page-container-standalone' : 'page-container'}>
      {authFlow === 'standalone' && (
        <Navbar
          userAvatar={auth0User?.picture}
          userName={auth0User?.name}
          onLogout={handleLogout}
          showNavLinks={true}
          organizationName={getClientName(config)}
        />
      )}
      <div className={`flex w-full flex-col gap-6 ${authFlow === 'standalone' ? 'px-6' : ''}`}>
        <OrderHeader
          mode="submit"
          patient={getPatientInfoFromForm(orderFormInfoData)}
          steps={orderSteps}
          onBack={handleCancel}
          onSubmit={handleSubmitOrder}
          submitDisabled={submitDisabled}
          isSubmitting={isSubmitting}
          backDisabled={isSubmitting}
          submitLabel={signingCopy.ctaLabel ?? undefined}
          showHelp={false}
          onDiscardOrder={passedSessionId ? openDiscardConfirm : undefined}
          containerClassName={authFlow === 'standalone' ? '-mx-6 border-b' : 'rounded-base border'}
        />
        <EpicSessionExpiryWarning />
        {signingCopy.treatment === 'docusign' ? (
          <div className="flex items-start gap-3 rounded-xl border border-success-100 bg-success-50 p-4">
            <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-success-600 text-white">
              <Check className="h-5 w-5" />
            </span>
            <div className="flex flex-col gap-1">
              <p className="text-sm font-bold leading-5 text-text-fg-success-strong sm:text-base">
                {signingCopy.title}
              </p>
              <p className="text-sm leading-5 text-text-fg-success-strong">{signingCopy.body}</p>
            </div>
          </div>
        ) : (
          <div className="info-banner flex items-start gap-3 rounded-xl border border-border-brand-subtle bg-background-brand-softer p-4">
            <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-brand-600 text-white">
              <InfoCircle className="h-5 w-5" />
            </span>
            <div className="flex flex-col gap-1">
              <p className="text-sm font-bold leading-5 text-brand-600 sm:text-base">{signingCopy.title}</p>
              <p className="text-sm leading-5 text-text-body">{signingCopy.body}</p>
            </div>
          </div>
        )}

        {/* Epic-only: encounter selection is optional for lab submit, but a
            selected encounter allows EHR ServiceRequest write-back via Redox. */}
        {isEpicFlow && orderFormInfoData && (
              <div className="flex flex-col gap-2">
                <label htmlFor="epic-encounter-select" className="text-sm font-medium text-text-heading">
                  Patient encounter
                </label>
                {patientEncountersLoading ? (
                  <p className="text-sm text-text-muted">Loading encounters…</p>
                ) : patientEncountersError ? (
                  <p className="text-sm text-red-600">{patientEncountersError}</p>
                ) : (
                  <select
                    id="epic-encounter-select"
                    value={selectedEncounterId}
                    disabled={isSubmitting}
                    onChange={(e) => {
                      const nextEncounterId = e.target.value
                      setSelectedEncounterId(nextEncounterId)
                    }}
                    className="w-full max-w-xl rounded-base border border-border-base px-3 py-2 text-sm text-text-body focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    <option value="">— Select an encounter (needed for EHR order write-back) —</option>
                    {patientEncounters.map((enc) => (
                      <option key={enc.id} value={enc.id}>
                        {enc.label}
                      </option>
                    ))}
                  </select>
                )}
                {!patientEncountersLoading &&
                  !patientEncountersError &&
                  patientEncounters.length === 0 &&
                  orderFormInfoData.patient.id?.trim() && (
                    <div
                      className="rounded-base border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900"
                      role="alert"
                    >
                      <p className="font-medium">No encounters available</p>
                      <p className="mt-1 text-amber-800">
                        The EHR did not return any encounters for this patient. You can still submit the lab order;
                        creating the matching EHR ServiceRequest (Redox) requires an encounter when your environment
                        has Redox enabled.
                      </p>
                    </div>
                  )}
              </div>
            )}

        {/* If the logged-in user has no NPI, they must select an NPI-bearing
            clinician before the order can be submitted. */}
        {missingCurrentUserNpi && (
          <div role="alert" className="rounded-base border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
            {isEpicFlow ? (
              <>
                Your account does not have an NPI on file. Search below and select a clinician with an NPI to
                submit this order on their behalf.
              </>
            ) : (
              <>
                Your account does not have an NPI on file. Choose an authorizing clinician from your organization
                below (NPI required).
              </>
            )}
          </div>
        )}

        {submitDegradedNotice && (
          <div
            role="status"
            className="flex items-start gap-3 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900"
          >
            <InfoCircle className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" aria-hidden="true" />
            <span>{submitDegradedNotice}</span>
          </div>
        )}

        {clinicalTextError && (
          <AlertBanner variant="error">{clinicalTextError}</AlertBanner>
        )}
        {submitError && (
          <AlertBanner variant="error" onClose={() => setSubmitError(null)} useInflectionIcon={true}>
            {submitError}
          </AlertBanner>
        )}
        {discardError && (
          <AlertBanner onClose={clearDiscardError} useInflectionIcon={true} variant="error">
            {discardError}
          </AlertBanner>
        )}

        {coveragePending && (
          <div
            role="status"
            aria-live="polite"
            className="flex items-center gap-2 rounded-base border border-border-base bg-background-secondary-medium px-4 py-3 text-sm text-text-muted"
          >
            <svg
              className="h-4 w-4 shrink-0 animate-spin text-brand-600"
              viewBox="0 0 24 24"
              fill="none"
              aria-hidden="true"
            >
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
              />
            </svg>
            <span>Checking coverage eligibility…</span>
          </div>
        )}

        {coverageWarning && !coveragePending && (
          <div
            role="alert"
            className="rounded-base border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900 dark:border-amber-900/40 dark:bg-amber-950/40 dark:text-amber-200"
          >
            {`${attestorName?.trim() && attestorName.trim() !== 'Unknown'
              ? attestorName.trim()
              : 'The authorizing clinician'} is not a provider credentialed with ${coverageWarningLabel} which is required to submit this order. Select an Authorizing Provider with ${coverageWarningLabelShort} credentials to continue.`}
          </div>
        )}

        {coverageSatisfied && !coverageWarning && !coveragePending && (
          <div
            role="status"
            className="flex items-center gap-2 rounded-base border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-900 dark:border-green-900/40 dark:bg-green-950/40 dark:text-green-200"
          >
            <Check className="h-5 w-5 shrink-0" aria-hidden="true" />
            <span>
              {`${attestorName?.trim() && attestorName.trim() !== 'Unknown'
                ? attestorName.trim()
                : 'The authorizing clinician'} is ${coverageSatisfiedLabel} credentialed for this order.`}
            </span>
          </div>
        )}

        {coverageNotApplicable && !coverageSatisfied && !coverageWarning && !coveragePending && (
          <div
            role="status"
            className="flex items-center gap-2 rounded-base border border-border-base bg-background-secondary-medium px-4 py-3 text-sm text-text-muted"
          >
            <InfoCircle className="h-5 w-5 shrink-0" aria-hidden="true" />
            <span>
              {`${coverageNotApplicableLabel} does not apply to this order because ${coverageNotApplicableReason} meet the coverage criteria, so no ${coverageNotApplicableLabel} credential requirement is enforced.`}
            </span>
          </div>
        )}

        {/* Authorizing Clinician card: always-visible signer-of-record display.
            "Change Authorizing Clinician" opens the existing Epic search or
            standalone org-practitioner picker via the same isOnBehalfOfChecked state. */}
        {orderFormInfoData && (
          <AuthorizingClinicianCard
                name={attestorName ?? ''}
                npi={attestorNpi ?? ''}
                roleTitle={attestorRoleTitle}
                onChangeClick={() => {
                  setSubmitOnBehalfOfAnother(true)
                  setSubmittingOnBehalfOf('')
                  setSearchResults([])
                  setShowSearchDropdown(false)
                  // Keep selectedAuthorizingProvider so the card still shows the last signer.
                  // Keep standaloneOrgPractitionerId so the empty option is not already selected —
                  // choosing "— Select a clinician —" would not fire onChange and could not revert.
                  // Clearing submittingOnBehalfOf only resets the Epic search box.
                }}
                disabled={isSubmitting}
                attestationLabel={submitOrderAttestationIntro}
                checked={attestedTerms}
                onCheckedChange={setAttestedTerms}
              >
                {isOnBehalfOfChecked && (
                  <div className="flex flex-col gap-3" ref={dropdownContainerRef}>
                    {isEpicFlow ? (
                      <>
                        <label
                          htmlFor="submit-on-behalf-of"
                          className="flex items-center text-sm font-medium text-text-heading"
                        >
                          Choose the authorizing clinician
                          <FieldMarker
                            state={resolveFieldMarkerState(true, submittingOnBehalfOf.trim().length > 0)}
                            fieldLabel="Authorizing clinician"
                            inlineOwesSwatch
                          />
                        </label>
                        <div className="relative">
                          <Search className="pointer-events-none absolute left-3 top-1/2 size-5 -translate-y-1/2 text-text-muted" />
                          <input
                            id="submit-on-behalf-of"
                            type="text"
                            value={submittingOnBehalfOf}
                            disabled={isSubmitting}
                            onChange={(e) => {
                              abortAttendingRead()
                              const next = e.target.value
                              setSubmittingOnBehalfOf(next)
                              if (
                                selectedAuthorizingProvider &&
                                next.trim() !== selectedAuthorizingProvider.authorizingProfessionalInfo.name
                              ) {
                                setSelectedAuthorizingProvider(null)
                              }
                            }}
                            onFocus={() => setShowSearchDropdown(true)}
                            onBlur={() => {
                              blurRevertTimeoutRef.current = setTimeout(() => closeSearchDropdown(), 150)
                            }}
                            placeholder="Search by clinician name"
                            className="w-full rounded-base border border-border-base py-2 pl-10 pr-3 text-sm text-text-body placeholder:text-text-muted focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500 disabled:cursor-not-allowed disabled:opacity-50"
                            autoComplete="off"
                          />
                          {showSearchDropdown && (
                            <div className="absolute left-0 right-0 top-full z-10 mt-1 max-h-60 overflow-auto rounded-base border border-border-base bg-white shadow-lg">
                              {isSearching ? (
                                <div className="px-3 py-4 text-center text-sm text-text-muted">Searching...</div>
                              ) : (
                                <>
                                  {providerName && currentUserHasNpi && (
                                    <button
                                      type="button"
                                      onClick={handleSelectCurrentUser}
                                      disabled={isSubmitting}
                                      className="block w-full px-3 py-2 text-left text-sm text-text-body hover:bg-background-secondary-medium disabled:cursor-not-allowed disabled:opacity-50"
                                    >
                                      Ordering User ({providerName})
                                    </button>
                                  )}
                                  {searchResults.length > 0 && (
                                    <>
                                      {providerName && currentUserHasNpi && (
                                        <div className="border-t border-border-base" />
                                      )}
                                      {searchResults.map((result, idx) => {
                                        // NPI state is only returned for practitioners with an NPI,
                                        // and only when the server can resolve it. When that state
                                        // matches the organization's state from the client config
                                        // (both normalized to USPS abbrev), the state code itself is
                                        // emphasized — no row-level highlight.
                                        const npiState = result.patientPhysician.npiState?.trim() ?? ''
                                        const npiStateMatchesOrg =
                                          !!orgState && normalizeUsState(npiState) === orgState
                                        return (
                                          <button
                                            key={`${result.patientPhysician.id}-${idx}`}
                                            type="button"
                                            onClick={() => handleAuthorizingProviderSelect(result)}
                                            disabled={isSubmitting}
                                            className="block w-full px-3 py-2 text-left text-sm text-text-body hover:bg-background-secondary-medium disabled:cursor-not-allowed disabled:opacity-50"
                                          >
                                            {result.authorizingProfessionalInfo.name}
                                            {/* roleTitle is config-derived and may be absent; only render when present. */}
                                            {result.patientPhysician.roleTitle?.trim() && (
                                              <span className="ml-2 text-text-muted">
                                                {result.patientPhysician.roleTitle.trim()}
                                              </span>
                                            )}
                                            {result.authorizingProfessionalInfo.npi && (
                                              <span className="ml-2 text-text-muted">
                                                NPI {result.authorizingProfessionalInfo.npi}
                                              </span>
                                            )}
                                            {/*
                                              NPPES-derived state for the practitioner's NPI. The
                                              contract ties `state` to NPI presence, so only render
                                              the state slot for results that have an NPI: show the
                                              value when known, otherwise a neutral "—" placeholder
                                              (state unknown — not found/looked-up in NPPES). Only
                                              the state code is emphasized when it matches the org.
                                            */}
                                            {result.authorizingProfessionalInfo.npi && (
                                              <span className="ml-2 text-text-muted">
                                                State:{' '}
                                                {npiState ? (
                                                  <span
                                                    className={
                                                      npiStateMatchesOrg
                                                        ? 'font-semibold text-brand-700'
                                                        : 'text-text-muted'
                                                    }
                                                    title={
                                                      npiStateMatchesOrg
                                                        ? 'Matches your organization’s state'
                                                        : undefined
                                                    }
                                                  >
                                                    {npiState}
                                                  </span>
                                                ) : (
                                                  <span title="State unknown" aria-label="State unknown">
                                                    —
                                                  </span>
                                                )}
                                              </span>
                                            )}
                                          </button>
                                        )
                                      })}
                                    </>
                                  )}
                                  {submittingOnBehalfOf.trim().length >= 2 && searchResults.length === 0 && (
                                    <div className="px-3 py-4 text-center text-sm text-text-muted">
                                      No providers found. Try a different search.
                                    </div>
                                  )}
                                </>
                              )}
                            </div>
                          )}
                        </div>
                      </>
                    ) : (
                      <>
                        {!orgId ? (
                          <p className="text-sm text-amber-800">
                            Your account is missing organization context. You cannot load organization practitioners until
                            your profile includes an organization.
                          </p>
                        ) : orgPractitionersLoading ? (
                          <p className="text-sm text-text-muted">Loading organization practitioners…</p>
                        ) : orgPractitionersError ? (
                          <p className="text-sm text-red-600">{orgPractitionersError}</p>
                        ) : (
                          <>
                            <label
                              htmlFor="standalone-org-practitioner"
                              className="flex items-center text-sm font-medium text-text-heading"
                            >
                              Authorizing clinician
                              <FieldMarker
                                state={resolveFieldMarkerState(
                                  true,
                                  standaloneOrgPractitionerId.trim().length > 0
                                )}
                                fieldLabel="Authorizing clinician"
                                inlineOwesSwatch
                              />
                            </label>
                            <p className="text-xs text-text-muted">
                              Practitioners with an NPI on file in your organization (
                              {getClientName(config) ?? 'your org'}).
                            </p>
                            <select
                              id="standalone-org-practitioner"
                              value={standaloneOrgPractitionerId}
                              disabled={isSubmitting}
                              onChange={(e) => {
                                const id = e.target.value
                                setStandaloneOrgPractitionerId(id)
                                if (!id) {
                                  setSelectedAuthorizingProvider(null)
                                  setSubmittingOnBehalfOf('')
                                  if (!cannotUncheckOnBehalfOf) {
                                    setSubmitOnBehalfOfAnother(false)
                                  }
                                  return
                                }
                                const entry = sortedOrgPractitioners.find((p) => p.practitioner_id === id)
                                if (entry) {
                                  const sel = organizationPractitionerToAuthorizingSelection(entry)
                                  setSelectedAuthorizingProvider(sel)
                                  setSubmittingOnBehalfOf(sel.authorizingProfessionalInfo.name)
                                }
                              }}
                              className="w-full rounded-base border border-border-base px-3 py-2 text-sm text-text-body focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500 disabled:cursor-not-allowed disabled:opacity-50"
                            >
                              <option value="">— Select a clinician —</option>
                              {sortedOrgPractitioners.map((p) => {
                                const label = organizationPractitionerDisplayName(p)
                                const npi = organizationPractitionerEntryNpi(p)
                                // roleTitle is data-derived (medical_degree) and may be absent; only append when present.
                                const roleTitle = organizationPractitionerRoleTitle(p)
                                return (
                                  <option key={p.organization_practitioner_reln_id} value={p.practitioner_id}>
                                    {label}
                                    {roleTitle && `, ${roleTitle}`} (NPI {npi})
                                  </option>
                                )
                              })}
                            </select>
                          </>
                        )}
                        {sortedOrgPractitioners.length === 0 && !orgPractitionersLoading && orgId && !orgPractitionersError && (
                          <p className="text-sm text-amber-800">
                            No practitioners with an NPI are linked to your organization. An administrator must add the
                            authorizing clinician before you can submit on their behalf.
                          </p>
                        )}
                      </>
                    )}
                    {submittingOnBehalfOf &&
                      submittingOnBehalfOf.trim() !== '' &&
                      submittingOnBehalfOf.trim() !== providerName &&
                      selectedAuthorizingProvider != null && (
                        <p className="text-sm text-amber-700">
                          By submitting this order I can attest I have received authorization from the specified medical
                          professional to perform this action.
                        </p>
                      )}
                  </div>
                )}
          </AuthorizingClinicianCard>
        )}

        {/* Fallback attestation checkbox when the clinician card is not rendered.
            The card is gated on orderFormInfoData, but submit is reachable via the
            session path even when orderFormInfoData is absent. This ensures the
            attestation control is always present whenever Submit is on the page,
            preventing a silent permanent block from !attestedTerms. */}
        {!orderFormInfoData && (
          <label className="flex cursor-pointer items-start gap-2">
            <input
              type="checkbox"
              checked={attestedTerms}
              onChange={(e) => setAttestedTerms(e.target.checked)}
              disabled={isSubmitting}
              className="mt-0.5 h-4 w-4 shrink-0 rounded border-border-base text-brand-600 focus:ring-brand-600 disabled:cursor-not-allowed"
            />
            <span className="select-none text-sm leading-5 text-text-body dark:text-gray-400">
              {submitOrderAttestationIntro}
            </span>
          </label>
        )}

        <TermsDisclosureCard
          title={clinicalAttestationTitle}
          summary={clinicalAttestationSummary}
        >
          <p className="whitespace-pre-wrap">{providerConsentText}</p>
        </TermsDisclosureCard>

        <TermsDisclosureCard
          title={inflectionAuthorizationTitle}
          summary={inflectionAuthorizationSummary}
        >
          <p className="whitespace-pre-wrap">{inflectionAuthorizationTerms}</p>
        </TermsDisclosureCard>

        {/* Read-only requisition preview so the user can inspect exactly what will
            be submitted before clicking Submit. */}
        <div className="bg-[#e4e4e7] px-6 pb-6 pt-4 dark:bg-zinc-800/80">
          {(storedCanonical || formData) && orderFormInfoData ? (
            <div className="mx-auto max-w-[820px] rounded-xl bg-white shadow-lg">
              <RequisitionFormView
                sessionId={passedSessionId}
                canonical={storedCanonical}
                formData={formData}
                orderData={orderFormInfoDataWithAuthorizing ?? orderFormInfoData}
                isEhrOrder={isEpicFlow}
                draftRefreshToken={draftPreviewToken}
                showHeader={false}
                showControlRow
                viewerClassName="h-[calc(100vh-520px)]"
                deferPdfRender={!selfProfileSettled}
              />
            </div>
          ) : (
            <div className="text-sm text-text-body">Requisition form is unavailable.</div>
          )}
        </div>
      </div>

      <OrderSuccessModal
        isOpen={showSuccessModal}
        onClose={handleSuccessClose}
        onReturnToDashboard={handleReturnToDashboard}
        onPrintOrderForm={handlePrintOrderForm}
        onGenerateShippingLabel={handleGenerateShippingLabel}
        isEhrFlow={isEpicFlow}
        signatureRequestedCopy={signatureRequestedCopy}
        variant={signatureRequestedCopy ? 'signature-requested' : undefined}
      />
      <OrderFormViewModal
        isOpen={isPrintFormModalOpen}
        onClose={handlePrintFormModalClose}
        canonical={storedCanonical}
        formData={formData}
        orderData={(submittedOrderData ?? orderFormInfoDataWithAuthorizing ?? orderFormInfoData) ?? null}
        // Submit has already stored this order's TRF, so print the stored document
        // rather than a second client-side render of it.
        orderId={submittedOrderId}
        caller={getCallerFromOrderData(submittedOrderData ?? orderFormInfoData ?? undefined)}
      />
      <ShippingLabelModal
        isOpen={isShippingLabelModalOpen}
        onClose={handleShippingLabelModalClose}
        state={shippingLabelState}
        onRetry={handleGenerateShippingLabel}
      />
      <DiscardOrderConfirmDialog
        isOpen={discardConfirmOpen}
        onClose={closeDiscardConfirm}
        onConfirm={performDiscard}
        isSubmitting={isDiscarding}
        patientName={patientDisplayName}
        patientMrn={orderFormInfoData?.patient?.mrn ?? ''}
        draftOwnerDisplayName={draftOwnerDisplayName}
        draftUpdatedAt={draftSessionMeta.updatedAt}
        currentUserId={currentUserId}
        draftOwnerUserId={draftSessionMeta.ownerUserId}
        confirmReady={confirmReady}
      />
    </div>
  )
}
